import React, { useState, useEffect } from 'react';
import apiClient from '../lib/apiClient';
import OrderModal from '../components/OrderModal';
import { Country, State, City } from 'country-state-city';
import { retryOrderPayment } from '../utils/retryPayment';
import { loadOrderDraft, clearOrderDraft } from '../utils/orderDraft';
import { buildWorkspaceBillingPrefill, normalizeWorkspaceUser } from '../utils/workspaceUser';
import { getPhoneRequirementMessage, normalizePhoneForCountry, validatePhoneForCountry } from '../utils/phoneValidation';
import '../styles/Dashboard.css';

const COMMON_PASSWORDS = new Set([
    'password', 'password123', 'qwerty', 'qwerty123', '123456', '12345678',
    '123456789', '1234567890', 'abc123', 'admin', 'admin123', 'welcome',
    'welcome123', 'letmein', 'iloveyou', 'monkey', 'dragon', 'passw0rd',
]);

const normalizePasswordForComparison = (password = '') =>
    password.normalize('NFKC').replace(/\s+/g, '').toLocaleLowerCase();

const getPasswordChecks = (password = '', confirmPassword = '') => ({
    hasMinLength: Array.from(password).length >= 12,
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[1-9]/.test(password),
    hasSpecialChar: /[^A-Za-z0-9]/.test(password),
    isNotCommonPassword: !COMMON_PASSWORDS.has(normalizePasswordForComparison(password)),
    hasPrintableCharactersOnly: /^[\x21-\x7E]+$/.test(password),
    matchesConfirmation: confirmPassword.length > 0 && password === confirmPassword,
});

const parseBooleanLike = (value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        return ['1', 'true', 'yes', 'y', 'verified'].includes(normalized);
    }
    return false;
};

const hasNonEmptyValue = (value) => String(value || '').trim().length > 0;

const PasswordRequirement = ({ met, label }) => {
    const color = met === true ? '#1E7A3E' : met === false ? '#D81F26' : '#6E6E6E';
    const icon = met === true ? '✓' : met === false ? '✕' : '○';
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color }}>
            <span>{icon}</span>
            <span>{label}</span>
        </div>
    );
};

const PLAN_CATALOG = {
    standard: {
        key: 'standard',
        name: 'Standard',
        summary: 'Core AI search, document intelligence, and monthly workspace billing.',
        features: ['AI document search', 'Recurring monthly subscription', 'Invoice history and support'],
    },
    pro: {
        key: 'pro',
        name: 'Pro',
        summary: 'Higher-volume workspace operations with stronger collaboration and support needs.',
        features: ['Expanded usage allowances', 'Priority billing operations', 'Faster production response'],
    },
    enterprise: {
        key: 'enterprise',
        name: 'Enterprise',
        summary: 'Custom workspace operations, governance, and dedicated rollout support.',
        features: ['Custom commercials', 'Security and compliance review', 'Enterprise onboarding support'],
    },
};

const normalizePlanKey = (value = '') => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized.includes('enterprise')) return 'enterprise';
    if (normalized.includes('pro')) return 'pro';
    return 'standard';
};

const getPlanMeta = (value = '') => PLAN_CATALOG[normalizePlanKey(value)] || PLAN_CATALOG.standard;
const PLAN_SEQUENCE = ['standard', 'pro', 'enterprise'];
const getPlanRank = (value = '') => PLAN_SEQUENCE.indexOf(normalizePlanKey(value));

const formatDashboardDate = (value, options = {}) => {
    if (!value) return 'Not scheduled';
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return 'Not scheduled';
    return dt.toLocaleDateString(undefined, options);
};

const formatCurrencyAmount = (amount, currency = 'INR') => {
    const numeric = Number(amount);
    if (!Number.isFinite(numeric)) return 'Not available';
    return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: (currency || 'INR').toUpperCase(),
        minimumFractionDigits: 2,
    }).format(numeric / 100);
};

const summarizePaymentMethod = (order = {}) => {
    if (order?.payment_vpa) {
        return `UPI (${order.payment_vpa})`;
    }
    if (order?.payment_card_last4) {
        const brand = order?.payment_card_network || 'Card';
        return `${brand} ending ${order.payment_card_last4}`;
    }
    if (order?.payment_method) {
        return String(order.payment_method).replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
    }
    return 'Managed through your saved Razorpay mandate';
};

const Dashboard = () => {
    const [user, setUser] = useState(null);
    const [orders, setOrders] = useState([]);
    const [localDraft, setLocalDraft] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [viewportWidth, setViewportWidth] = useState(
        typeof window !== 'undefined' ? window.innerWidth : 1440
    );

    // Modal state
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [orderInitialValues, setOrderInitialValues] = useState({});
    const [modalError, setModalError] = useState('');
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        phoneCountry: 'IN',
        organizationName: '',
        accountType: 'individual',
        address: '',
        city: '',
        state: '', // This will now store isoCode
        country: '', // This will now store isoCode
        zipCode: '',
        industry: '',
        teamSize: '',
        description: ''
    });

    // Location lists
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [retryingOrderId, setRetryingOrderId] = useState(null);
    const [deletingOrderId, setDeletingOrderId] = useState(null);
    const [cancelingSubscriptionId, setCancelingSubscriptionId] = useState(null);
    const [retryMethodPending, setRetryMethodPending] = useState(null); // order waiting for method pick
    const [orderAction, setOrderAction] = useState(null);
    const [showPhoneVerifyModal, setShowPhoneVerifyModal] = useState(false);
    const [phoneVerifyCode, setPhoneVerifyCode] = useState('');
    const [phoneVerifyMessage, setPhoneVerifyMessage] = useState('');
    const [phoneVerifyError, setPhoneVerifyError] = useState('');
    const [phoneSending, setPhoneSending] = useState(false);
    const [phoneVerifying, setPhoneVerifying] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const [invoiceError, setInvoiceError] = useState('');
    const [invoicesLoading, setInvoicesLoading] = useState(false);
    const [emailVerifyMessage, setEmailVerifyMessage] = useState('');
    const [emailVerifyError, setEmailVerifyError] = useState('');
    const [emailSending, setEmailSending] = useState(false);
    const [paymentStage, setPaymentStage] = useState('idle'); // idle, processing, verifying, success, error
    const [statusMessage, setStatusMessage] = useState('');
    const [paymentError, setPaymentError] = useState('');
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [passwordSaving, setPasswordSaving] = useState(false);
    const [passwordMessage, setPasswordMessage] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [activeView, setActiveView] = useState('overview');
    const [launchOrderIntent, setLaunchOrderIntent] = useState(null);
    const [supportThreads, setSupportThreads] = useState([]);
    const [supportMessages, setSupportMessages] = useState([]);
    const [supportSelectedId, setSupportSelectedId] = useState(null);
    const [supportDraft, setSupportDraft] = useState('');
    const [supportLoading, setSupportLoading] = useState(false);
    const [supportSending, setSupportSending] = useState(false);
    const [supportError, setSupportError] = useState('');

    const isSmallMobile = viewportWidth <= 374;
    const isStandardMobile = viewportWidth > 374 && viewportWidth <= 424;
    const isLargeMobile = viewportWidth > 424 && viewportWidth <= 599;
    const isTabletPortrait = viewportWidth > 599 && viewportWidth <= 768;
    const isTabletLandscape = viewportWidth > 768 && viewportWidth <= 1024;
    const isMobile = viewportWidth <= 599;
    const isTabletOrBelow = viewportWidth <= 768;
    const isNarrow = viewportWidth <= 1024;

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const handleResize = () => setViewportWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        setCountries(Country.getAllCountries());
    }, []);

    useEffect(() => {
        if (formData.country) {
            setStates(State.getStatesOfCountry(formData.country));
        }
    }, [formData.country]);

    useEffect(() => {
        if (formData.country && formData.state) {
            setCities(City.getCitiesOfState(formData.country, formData.state));
        } else {
            setCities([]);
        }
    }, [formData.country, formData.state]);

    useEffect(() => {
        if (activeView === 'conversations') {
            loadSupportThreads();
        }
    }, [activeView]);

    useEffect(() => {
        if (supportSelectedId) {
            loadSupportMessages(supportSelectedId);
        } else {
            setSupportMessages([]);
        }
    }, [supportSelectedId]);

    // Logging utility
    const emitClientLog = (payload) => {
        try {
            const body = JSON.stringify(payload);
            const blob = new Blob([body], { type: 'application/json' });
            if (navigator.sendBeacon) {
                navigator.sendBeacon('/api/client-log', blob);
            } else {
                fetch('/api/client-log', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body,
                    credentials: 'include'
                });
            }
        } catch (sendErr) {
            console.error('Client log dispatch failed:', sendErr);
        }
    };

    const logError = (err, context) => {
        const extra = {
            context,
            url: window.location.href,
            stack: err?.stack,
            status: err?.response?.status,
            endpoint: err?.config?.url,
            method: err?.config?.method,
            response: err?.response?.data
        };
        emitClientLog({
            level: 'error',
            message: err?.message || String(err),
            extra,
        });
    };

    const loadSupportThreads = async () => {
        setSupportLoading(true);
        setSupportError('');
        try {
            const res = await apiClient.get('/api/conversations');
            const rows = res.data?.conversations || [];
            setSupportThreads(rows);
            setSupportSelectedId((current) => current || rows[0]?.id || null);
        } catch (err) {
            setSupportError('Unable to load support conversations. Please refresh and try again.');
            logError(err, 'loadSupportThreads');
        } finally {
            setSupportLoading(false);
        }
    };

    const loadSupportMessages = async (conversationId) => {
        setSupportError('');
        try {
            const res = await apiClient.get(`/api/conversations/${conversationId}/messages`);
            setSupportMessages(res.data?.messages || []);
        } catch (err) {
            setSupportError('Unable to load this support thread.');
            logError(err, 'loadSupportMessages');
        }
    };

    const createSupportThread = async () => {
        const today = new Date().toISOString().slice(0, 10);
        const phone = user?.phone || formData.phone || '0000000000';
        const phoneCountry = user?.phoneCountry || user?.phonecountry || formData.phoneCountry || 'IN';
        const payload = {
            product: currentPlanMeta?.key || currentPlanMeta?.id || 'starter_workspace',
            workspace_name: user?.name || formData.name || 'Corpusio workspace',
            primary_channel: 'Dashboard support',
            target_launch_date: today,
            primary_languages: 'English',
            team_size: '1',
            automation_goals: 'Support request from the Corpusio dashboard. Please review the customer message and continue the conversation here.',
            contact_phone: phone,
            contact_phone_country: phoneCountry,
            escalation_phone: phone,
            escalation_phone_country: phoneCountry,
            contact_email: user?.email || formData.email || '',
        };
        setSupportLoading(true);
        setSupportError('');
        try {
            const res = await apiClient.post('/api/create_conversation', payload);
            const conversation = res.data?.conversation;
            await loadSupportThreads();
            if (conversation?.id) {
                setSupportSelectedId(conversation.id);
            }
        } catch (err) {
            setSupportError('Unable to start support chat. Please complete your profile phone and email first.');
            logError(err, 'createSupportThread');
        } finally {
            setSupportLoading(false);
        }
    };

    const sendSupportMessage = async (event) => {
        event?.preventDefault?.();
        const body = supportDraft.trim();
        if (!body || !supportSelectedId) return;
        setSupportSending(true);
        setSupportError('');
        try {
            await apiClient.post(`/api/conversations/${supportSelectedId}/messages`, { message: body });
            setSupportDraft('');
            await Promise.all([loadSupportMessages(supportSelectedId), loadSupportThreads()]);
        } catch (err) {
            setSupportError('Unable to send message. Please try again.');
            logError(err, 'sendSupportMessage');
        } finally {
            setSupportSending(false);
        }
    };

    useEffect(() => {
        fetchUserAndOrders();
        setLocalDraft(loadOrderDraft());

        const params = new URLSearchParams(window.location.search);
        const requestedView = params.get('view');
        if (requestedView) {
            setActiveView(requestedView);
        }
        if (params.get('newOrder')) {
            setLaunchOrderIntent({
                productName: params.get('product') || '',
                productType: params.get('plan') || ''
            });
        }
    }, []);

    const refreshDraftState = () => {
        setLocalDraft(loadOrderDraft());
    };

    const fetchUserAndOrders = async () => {
        setInvoicesLoading(true);
        try {
            const [userRes, ordersRes] = await Promise.all([
                apiClient.get('/api/user'),
                apiClient.get('/api/user/orders'),
            ]);

            const userData = normalizeWorkspaceUser(userRes.data || {});
            const ordersPayload = ordersRes.data;
            const normalizedOrders = Array.isArray(ordersPayload)
                ? ordersPayload
                : ordersPayload?.orders || [];
            const signupMethod = String(userData?.signup_method || userData?.signupmethod || '').toLowerCase();
            const effectiveEmailVerified = signupMethod.includes('google')
                || hasNonEmptyValue(userData?.google_id || userData?.googleId)
                || parseBooleanLike(userData?.email_verified ?? userData?.emailverified)
                || hasNonEmptyValue(userData?.emailverifiedat || userData?.email_verified_at);

            setUser(userData);
            setOrders(normalizedOrders);
            if (effectiveEmailVerified) {
                setEmailVerifyError('');
                setEmailVerifyMessage('');
                setOrderAction(prev => (
                    prev?.text === 'Please verify your email address to continue.'
                        ? null
                        : prev
                ));
            }

            try {
                const invoicesRes = await apiClient.get('/api/invoices');
                const invoicesPayload = invoicesRes.data?.invoices || [];
                setInvoices(invoicesPayload);
                setInvoiceError('');
            } catch (invoiceErr) {
                console.error('Error fetching invoices:', invoiceErr);
                setInvoices([]);
                setInvoiceError('Invoices are temporarily unavailable. Please refresh later.');
                logError(invoiceErr, 'fetchInvoices');
            }

            const loadedAccountType = userData.account_type || userData.accounttype || (userData.organization_name || userData.organizationname ? 'company' : 'individual');
            const profileNeedsAttention = !userData.phone || !userData.address || !userData.city || !userData.state || !userData.country || !userData.zip_code || (loadedAccountType === 'company' && !(userData.organization_name || userData.organizationname));

            if (userData && profileNeedsAttention) {
                setShowProfileModal(true);
                // Pre-fill form
                setFormData(prev => ({
                    ...prev,
                    name: userData.name || '',
                    phone: normalizePhoneForCountry(
                        userData.phone || '',
                        userData.phoneCountry || userData.phoneCountryCode || userData.phonecountry || 'IN',
                        Country.getAllCountries()
                    ) || (userData.phone || ''),
                    phoneCountry: userData.phoneCountry || userData.phoneCountryCode || userData.phonecountry || 'IN',
                    organizationName: userData.organization_name || userData.organizationname || '',
                    accountType: loadedAccountType,
                    address: userData.address || '',
                    city: userData.city || '',
                    state: userData.state || '',
                    country: userData.country || '',
                    zipCode: userData.zip_code || '',
                    industry: userData.industry || '',
                    teamSize: userData.team_size || '',
                    description: userData.description || ''
                }));
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            logError(error, 'fetchUserAndOrders');

            if (error.response?.status === 401) {
                window.location.href = '/login';
            } else {
                setError('Failed to load dashboard data. Please try refreshing.');
            }
        } finally {
            setLoading(false);
            setInvoicesLoading(false);
        }
    };

    const isPhoneVerified = parseBooleanLike(user?.phoneverified ?? user?.phone_verified);
    const signupMethod = String(user?.signup_method || user?.signupmethod || '').toLowerCase();
    const isOauthAccount = signupMethod.includes('google') || hasNonEmptyValue(user?.google_id || user?.googleId);
    const isEmailVerified = parseBooleanLike(user?.email_verified ?? user?.emailverified)
        || hasNonEmptyValue(user?.emailverifiedat || user?.email_verified_at);
    const needsEmailVerification = !isOauthAccount && !isEmailVerified;
    const hasValue = (value) => String(value || '').trim().length > 0;
    const userAccountType = String(user?.account_type || user?.accounttype || '').toLowerCase()
        || (hasValue(user?.organization_name || user?.organizationname) ? 'company' : 'individual');
    const requiresOrganization = userAccountType === 'company';
    const cityValue = user?.city || user?.address_city || user?.addresscity;
    const stateValue = user?.state || user?.address_state || user?.addressstate;
    const countryValue = user?.country || user?.address_country || user?.addresscountry;
    const zipValue = user?.zipCode || user?.zipcode || user?.address_zipcode || user?.addresszipcode;
    const draftCount = localDraft ? 1 : 0;
    const paidOrders = orders.filter((order) => ['paid', 'completed', 'captured'].includes(String(order.status || '').toLowerCase())).length;
    const openOrders = orders.filter((order) => ['created', 'failed', 'pending', 'reauthorization_required'].includes(String(order.status || '').toLowerCase())).length + draftCount;
    const invoiceCount = invoices.length;
    const terminalSubscriptionStatuses = ['cancelled', 'expired', 'completed', 'superseded'];
    const isTerminalSubscriptionOrder = (order = {}) => {
        const orderStatus = String(order.status || '').toLowerCase();
        const subscriptionStatus = String(order.subscription_status || '').toLowerCase();
        return terminalSubscriptionStatuses.includes(orderStatus) || terminalSubscriptionStatuses.includes(subscriptionStatus);
    };
    const sortedOrders = [...orders].sort((a, b) => new Date(b.created_at || b.date || 0) - new Date(a.created_at || a.date || 0));
    const latestPaidOrder = sortedOrders.find((order) => ['paid', 'completed', 'captured'].includes(String(order.status || '').toLowerCase()) && !isTerminalSubscriptionOrder(order));
    const activeSubscriptionOrder = sortedOrders.find((order) => {
        const subscriptionStatus = String(order.subscription_status || '').toLowerCase();
        const orderStatus = String(order.status || '').toLowerCase();
        return Boolean(order.subscription_id)
            && !terminalSubscriptionStatuses.includes(subscriptionStatus)
            && !terminalSubscriptionStatuses.includes(orderStatus);
    });
    const currentSubscriptionOrder = activeSubscriptionOrder || latestPaidOrder;
    const hasActiveSubscription = Boolean(activeSubscriptionOrder);
    const currentPlanMeta = getPlanMeta(currentSubscriptionOrder?.product_name || currentSubscriptionOrder?.product);
    const currentPlanFeatures = Array.isArray(currentPlanMeta.features) ? currentPlanMeta.features : [];
    const hasPlan = Boolean(currentSubscriptionOrder);
    const activePlanKey = hasActiveSubscription ? currentPlanMeta.key : '';
    const planStatus = String(currentSubscriptionOrder?.subscription_status || currentSubscriptionOrder?.status || '').toLowerCase();
    const planRenewalDate = currentSubscriptionOrder?.subscription_next_charge_at;
    const planRenewalDueNow = Boolean(currentSubscriptionOrder?.subscription_payment_due_now);
    const planRenewalDueSoon = Boolean(currentSubscriptionOrder?.subscription_payment_due_within_24h);
    const livePaymentStatus = String(currentSubscriptionOrder?.subscription_last_charge_status || planStatus || '').toLowerCase();
    const pendingPlanKeyRaw = currentSubscriptionOrder?.pending_plan_key || currentSubscriptionOrder?.pending_product_key || '';
    const pendingPlanMeta = pendingPlanKeyRaw ? getPlanMeta(pendingPlanKeyRaw) : null;
    const pendingPlanName = currentSubscriptionOrder?.pending_plan_label || pendingPlanMeta?.name || '';
    const pendingPlanEffectiveAt = currentSubscriptionOrder?.pending_change_effective_at;
    const hasPendingDowngrade = String(currentSubscriptionOrder?.pending_change_type || '').toLowerCase() === 'downgrade' && Boolean(pendingPlanName);
    const billingIntervalLabel = String(currentSubscriptionOrder?.subscription_frequency || 'monthly').replace(/_/g, ' ');
    const paymentMethodSummary = summarizePaymentMethod(currentSubscriptionOrder || latestPaidOrder || {});
    const canCancelCurrentSubscription = Boolean(
        activeSubscriptionOrder?.can_cancel_subscription && activeSubscriptionOrder?.subscription_id
    );
    const savedPaymentHint = currentSubscriptionOrder?.payment_card_last4
        ? `${currentSubscriptionOrder.payment_card_network || 'Card'} saved for recurring billing`
        : currentSubscriptionOrder?.payment_vpa
            ? `UPI mandate stored as ${currentSubscriptionOrder.payment_vpa}`
            : 'Recurring billing uses the saved Razorpay mandate from your last successful authorization';
    const latestInvoice = invoices[0] || null;
    const teamSeatsSummary = userAccountType === 'company'
        ? (user?.team_size || user?.teamsize || 'Owner seat active')
        : 'Single-user workspace';
    const teamSectionBadge = userAccountType === 'company'
        ? (user?.team_size || user?.teamsize || 'Team')
        : 'Owner';
    const workspaceLocation = [
        cityValue,
        states.find((state) => state.isoCode === stateValue)?.name || stateValue,
        countries.find((country) => country.isoCode === countryValue)?.name || countryValue,
    ].filter(Boolean).join(', ') || 'Not configured';
    const activeSubscriptions = orders.filter((order) => {
        const status = String(order.subscription_status || '').toLowerCase();
        const orderStatus = String(order.status || '').toLowerCase();
        return Boolean(order.subscription_id)
            && !terminalSubscriptionStatuses.includes(status)
            && !terminalSubscriptionStatuses.includes(orderStatus);
    }).length;
    const retryRequiredCount = orders.filter((order) => {
        const status = String(order.status || '').toLowerCase();
        return ['created', 'failed', 'pending', 'reauthorization_required'].includes(status);
    }).length;
    const invoiceMap = invoices.reduce((acc, invoice) => {
        if (invoice?.orderid) {
            acc[invoice.orderid] = invoice;
        }
        return acc;
    }, {});
    const readinessItems = [
        { label: 'Full name', complete: hasValue(user?.name), action: 'Add your full name' },
        { label: 'Email verified', complete: isOauthAccount || isEmailVerified, action: 'Verify email' },
        { label: 'Phone verified', complete: isPhoneVerified, action: 'Verify phone' },
        ...(requiresOrganization
            ? [{ label: 'Organization name', complete: hasValue(user?.organization_name || user?.organizationname), action: 'Add organization' }]
            : []),
        { label: 'Street address', complete: hasValue(user?.address || user?.address_line1 || user?.addressline1), action: 'Add address' },
        { label: 'City', complete: hasValue(cityValue), action: 'Add city' },
        { label: 'State / region', complete: hasValue(stateValue), action: 'Add state' },
        { label: 'Country', complete: hasValue(countryValue), action: 'Add country' },
        { label: 'Postal code', complete: hasValue(zipValue), action: 'Add postal code' },
    ];
    const completedReadinessItems = readinessItems.filter((item) => item.complete).length;
    const missingReadinessItems = readinessItems.filter((item) => !item.complete);
    const profileCompletion = Math.round((completedReadinessItems / readinessItems.length) * 100);
    const readinessSummary = missingReadinessItems.length
        ? `Missing: ${missingReadinessItems.map((item) => item.label).join(', ')}.`
        : 'All required billing and verification details are complete.';
    const nextStep = needsEmailVerification
        ? 'Verify your email to unlock payments.'
        : !isPhoneVerified
            ? 'Verify your phone before your next payment.'
            : missingReadinessItems.length
                ? missingReadinessItems[0].action
                : openOrders
                    ? 'Resolve the billing item that needs payment attention.'
                    : 'Your workspace is ready for production use.';
    const dashboardSections = [
        {
            id: 'overview',
            label: 'Overview',
            description: 'Daily workspace health, plan status, and recommended actions',
            badge: hasPlan ? currentPlanMeta.name : 'Setup',
        },
        {
            id: 'knowledge',
            label: 'Knowledge Sources',
            description: 'Content ingestion, sync readiness, and source coverage',
            badge: hasPlan ? 'Ready' : 'Pending',
        },
        {
            id: 'agents',
            label: 'AI Workflows',
            description: 'Workspace automations, assistants, and operating playbooks',
            badge: 'Drafting',
        },
        {
            id: 'conversations',
            label: 'Conversations',
            description: 'Answer activity, human follow-up, and unresolved requests',
            badge: openOrders ? `${openOrders} open` : 'Quiet',
        },
        {
            id: 'analytics',
            label: 'Analytics',
            description: 'Adoption, billing signals, and operational health',
            badge: invoiceCount ? `${invoiceCount} invoices` : 'New',
        },
        {
            id: 'billing',
            label: 'Usage & Billing',
            description: 'Current subscription, payment method, invoices, and plan controls',
            badge: hasPlan ? currentPlanMeta.name : 'Choose plan',
        },
        {
            id: 'team',
            label: 'Team & Access',
            description: 'Seats, ownership, and workspace role structure',
            badge: teamSectionBadge,
        },
        {
            id: 'integrations',
            label: 'Integrations',
            description: 'Connectors, ingestion paths, and rollout readiness',
            badge: 'Available',
        },
        {
            id: 'security',
            label: 'Security & Compliance',
            description: 'Verification, password health, and trust controls',
            badge: needsEmailVerification || !isPhoneVerified ? 'Action needed' : 'Healthy',
        },
        {
            id: 'settings',
            label: 'Workspace Settings',
            description: 'Workspace identity, billing profile, and operating preferences',
            badge: missingReadinessItems.length ? `${missingReadinessItems.length} missing` : 'Ready',
        },
    ];

    useEffect(() => {
        if (loading || !launchOrderIntent) return;

        if (hasActiveSubscription) {
            const requestedPlan = normalizePlanKey(
                launchOrderIntent.productType || launchOrderIntent.productName || ''
            );
            if (requestedPlan && requestedPlan !== activePlanKey) {
                handleOrderModalOpen({
                    productType: PLAN_CATALOG[requestedPlan]?.name || 'Standard',
                    quantity: 1,
                    mode: 'plan_change',
                    planChangeForOrder: activeSubscriptionOrder?.id,
                    currentPlanName: currentPlanMeta.name,
                    pendingPlanName,
                    pendingChangeEffectiveAt: pendingPlanEffectiveAt,
                });
                setOrderAction(null);
                setLaunchOrderIntent(null);
                window.history.replaceState({}, document.title, '/dashboard');
                return;
            }

            setActiveView('billing');
            setOrderAction({
                type: 'error',
                text: `This workspace already has the ${currentPlanMeta.name} plan. Manage renewal or change the plan from Usage & Billing instead of starting a second subscription.`,
            });
            setLaunchOrderIntent(null);
            window.history.replaceState({}, document.title, '/dashboard');
            return;
        }

        handleOrderModalOpen(launchOrderIntent);
        setLaunchOrderIntent(null);
        window.history.replaceState({}, document.title, '/dashboard');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, launchOrderIntent, hasActiveSubscription, currentPlanMeta.name, activePlanKey, activeSubscriptionOrder?.id, pendingPlanName, pendingPlanEffectiveAt]);

    const handleRequirePhoneVerification = () => {
        setActiveView('security');
        setOrderAction({ type: 'error', text: 'Verify the billing phone number before changing the subscription or retrying billing.' });
        setShowPhoneVerifyModal(true);
    };

    const handleRequireEmailVerification = () => {
        setActiveView('security');
        setOrderAction({ type: 'error', text: 'Verify the workspace email before we can continue with subscription or billing actions.' });
    };

    const handleSendEmailVerification = async () => {
        setEmailVerifyError('');
        setEmailVerifyMessage('');
        setEmailSending(true);
        try {
            const response = await apiClient.post('/api/resend-email-verification', {});
            setEmailVerifyMessage(response.data?.message || 'Verification link sent. Check your inbox.');
        } catch (err) {
            console.error('resend_email_verification failed', err);
            setEmailVerifyError(err?.response?.data?.error || 'Unable to send verification link. Please try again.');
            logError(err, 'resendEmailVerification');
        } finally {
            setEmailSending(false);
        }
    };

    const sendPhoneVerificationCode = async () => {
        setPhoneVerifyError('');
        setPhoneVerifyMessage('');
        setPhoneSending(true);
        try {
            const response = await apiClient.post('/api/request-phone-verification', {});
            setPhoneVerifyMessage(response.data?.message || 'Verification code sent. Check your phone or email.');
        } catch (err) {
            console.error('request_phone_verification failed', err);
            setPhoneVerifyError(err?.response?.data?.error || 'Unable to send verification code. Please try again.');
            logError(err, 'requestPhoneVerification');
        } finally {
            setPhoneSending(false);
        }
    };

    const handleVerifyPhoneCodeSubmit = async (e) => {
        e.preventDefault();
        if (phoneVerifyCode.trim().length !== 6) {
            setPhoneVerifyError('Enter the 6-digit verification code.');
            return;
        }
        setPhoneVerifyError('');
        setPhoneVerifying(true);
        try {
            const payload = { code: phoneVerifyCode.trim() };
            const response = await apiClient.post('/api/verify-phone-code', payload);
            setPhoneVerifyMessage(response.data?.message || 'Phone number verified.');
            setPhoneVerifyCode('');
            await fetchUserAndOrders();
            setShowPhoneVerifyModal(false);
            setOrderAction({ type: 'success', text: 'Phone number verified. You can now continue with payments.' });
        } catch (err) {
            console.error('verify_phone_code failed', err);
            setPhoneVerifyError(err?.response?.data?.error || 'Unable to verify code. Please try again.');
            logError(err, 'verifyPhoneCode');
        } finally {
            setPhoneVerifying(false);
        }
    };

    const handleModalSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setModalError('');

        try {
            const nameLetters = (formData.name || '').replace(/[^A-Za-z ]/g, '').trim();
            if (nameLetters.length < 5) {
                setModalError('Please enter your full name (at least 5 letters, letters and spaces only).');
                return;
            }
            if (formData.accountType === 'company' && !formData.organizationName.trim()) {
                setModalError('Organization name is required for company accounts.');
                return;
            }
            const phoneValidation = validatePhoneForCountry(formData.phone, formData.phoneCountry, countries);
            if (!phoneValidation.isValid) {
                setModalError(phoneValidation.message);
                return;
            }
            const profilePayload = {
                ...formData,
                phone: phoneValidation.normalized,
                organizationName: formData.accountType === 'company' ? formData.organizationName : '',
                industry: formData.accountType === 'company' ? formData.industry : '',
                teamSize: formData.accountType === 'company' ? formData.teamSize : '',
            };
            const res = await apiClient.post('/api/user/update', profilePayload);
            const updatedUser = normalizeWorkspaceUser(res.data?.user || res.data || {});
            setUser(updatedUser);
            setShowProfileModal(false);
        } catch (err) {
            console.error('Update failed', err);
            setModalError(err?.response?.data?.error || err?.message || 'Failed to save profile. Please try again.');
            logError(err, 'updateProfile');
        } finally {
            setSaving(false);
        }
    };

    const handleModalChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            if (name === 'accountType') {
                const next = { ...prev, accountType: value };
                if (value !== 'company') {
                    next.organizationName = '';
                    next.industry = '';
                    next.teamSize = '';
                }
                return next;
            }
            if (name === 'country') {
                return { ...prev, country: value, state: '', city: '' };
            }
            if (name === 'state') {
                return { ...prev, state: value, city: '' };
            }
            return { ...prev, [name]: value };
        });
    };

    const handleRetryPayment = (order) => {
        if (!order?.id || !user) return;
        if (needsEmailVerification) {
            handleRequireEmailVerification();
            return;
        }
        if (!isPhoneVerified) {
            setOrderAction({ type: 'error', text: 'Please verify your phone number before retrying payment.' });
            handleRequirePhoneVerification();
            return;
        }
        // Show method picker before creating the retry order
        setRetryMethodPending(order);
    };

    const executeRetryPayment = async (order, method) => {
        setRetryMethodPending(null);
        setOrderAction(null);
        setRetryingOrderId(order.id);
        setPaymentStage('processing');
        setStatusMessage('Initializing retry payment…');
        setPaymentError('');

        try {
            await retryOrderPayment(order.id, user, { productName: order.product_name || order.product }, method);
            setPaymentStage('success');
            setStatusMessage('Payment successful! Redirecting…');
            setOrderAction({ type: 'success', text: 'Payment successful! Redirecting…' });
            await fetchUserAndOrders();
            setTimeout(() => setPaymentStage('idle'), 2000);
        } catch (err) {
            console.error('Retry payment failed', err);
            const message = err?.response?.data?.error || err.message || 'Unable to retry payment. Please try again.';
            setPaymentError(message);
            setPaymentStage('error');
            setOrderAction({ type: 'error', text: message });
            if (err?.requiresEmailVerification || err?.response?.data?.requiresemailverification || err?.response?.data?.requires_email_verification) {
                handleRequireEmailVerification();
            }
            if (err?.requiresPhoneVerification || err?.response?.data?.requiresphoneverification || err?.response?.data?.requires_phone_verification) {
                handleRequirePhoneVerification();
            }
        } finally {
            setRetryingOrderId(null);
        }
    };

    const handleDeleteOrder = async (order) => {
        if (!order?.id) {
            return;
        }
        const confirmed = window.confirm('Delete this order draft? This cannot be undone.');
        if (!confirmed) return;
        setOrderAction(null);
        setDeletingOrderId(order.id);
        try {
            await apiClient.post(`/api/orders/${order.id}/delete`, {});
            setOrderAction({ type: 'success', text: 'Order deleted.' });
            await fetchUserAndOrders();
        } catch (err) {
            console.error('Delete order failed', err);
            const message = err?.response?.data?.error || err.message || 'Unable to delete order.';
            setOrderAction({ type: 'error', text: message });
        } finally {
            setDeletingOrderId(null);
        }
    };

    const handleCancelSubscription = async (order) => {
        if (!order?.id || !order?.subscription_id) {
            return;
        }

        const confirmed = window.confirm(
            'Cancel this subscription? Future recurring charges will stop, but your current paid period will remain available.'
        );
        if (!confirmed) return;

        setOrderAction(null);
        setCancelingSubscriptionId(order.subscription_id);
        try {
            const response = await apiClient.post(`/api/orders/${order.id}/cancel-subscription`, {});
            setOrderAction({
                type: 'success',
                text: response?.data?.message || 'Subscription cancelled. Future recurring charges have been stopped.',
            });
            await fetchUserAndOrders();
        } catch (err) {
            console.error('Cancel subscription failed', err);
            const message = err?.response?.data?.error || err.message || 'Unable to cancel subscription.';
            setOrderAction({ type: 'error', text: message });
        } finally {
            setCancelingSubscriptionId(null);
        }
    };

    // Open Profile Modal for editing
    const handleEditProfile = () => {
        setActiveView('settings');
        const accountType = user.account_type || user.accounttype || (user.organization_name || user.organizationname ? 'company' : 'individual');
        const phoneCountry = user.phoneCountry || user.phoneCountryCode || user.phonecountry || 'IN';
        setFormData({
            name: user.name || '',
            phone: normalizePhoneForCountry(user.phone || '', phoneCountry, countries) || (user.phone || ''),
            phoneCountry,
            organizationName: user.organization_name || user.organizationname || '',
            accountType,
            address: user.address || '',
            city: user.city || '',
            state: user.state || '',
            country: user.country || '',
            zipCode: user.zip_code || '',
            industry: user.industry || '',
            teamSize: user.team_size || '',
            description: user.description || ''
        });
        setShowProfileModal(true);
    };

    const passwordChecks = getPasswordChecks(passwordForm.newPassword, passwordForm.confirmPassword);
    const passwordMeetsPolicy =
        passwordChecks.hasMinLength &&
        passwordChecks.hasLowercase &&
        passwordChecks.hasNumber &&
        passwordChecks.hasSpecialChar &&
        passwordChecks.isNotCommonPassword &&
        passwordChecks.hasPrintableCharactersOnly;

    const handlePasswordSubmit = async (event) => {
        event.preventDefault();
        setPasswordError('');
        setPasswordMessage('');

        if (((!isOauthAccount && !passwordForm.currentPassword) || !passwordForm.newPassword || !passwordForm.confirmPassword)) {
            setPasswordError('Please fill in all password fields.');
            return;
        }
        if (!passwordMeetsPolicy) {
            setPasswordError('Password does not meet the security requirements.');
            return;
        }
        if (!passwordChecks.matchesConfirmation) {
            setPasswordError('Retyped password must match.');
            return;
        }

        setPasswordSaving(true);
        try {
            const response = await apiClient.post('/api/user/change-password', passwordForm);
            setPasswordMessage(response.data?.message || 'Password changed successfully.');
            setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (err) {
            setPasswordError(err.response?.data?.error || err.message || 'Unable to update password.');
            logError(err, 'changePassword');
        } finally {
            setPasswordSaving(false);
        }
    };

    const handleOrderSuccess = () => {
        fetchUserAndOrders(); // Refresh list
        refreshDraftState();
    };

    const handleOrderModalOpen = (initial = {}) => {
        const isPlanChangeIntent = initial?.mode === 'plan_change' && Boolean(initial?.planChangeForOrder);
        if (needsEmailVerification) {
            handleRequireEmailVerification();
            return;
        }
        if (!isPhoneVerified) {
            handleRequirePhoneVerification();
            return;
        }
        if (hasActiveSubscription && !isPlanChangeIntent) {
            setActiveView('billing');
            setOrderAction({
                type: 'error',
                text: `This workspace already has the ${currentPlanMeta.name} plan. Use Usage & Billing to manage or cancel it before starting another plan.`,
            });
            return;
        }
        const profilePrefill = buildWorkspaceBillingPrefill(user || {});
        const mergedInitialValues = { ...initial };
        Object.entries(profilePrefill).forEach(([key, value]) => {
            const currentValue = mergedInitialValues[key];
            const shouldBackfill =
                currentValue === undefined ||
                currentValue === null ||
                (typeof currentValue === 'string' && !currentValue.trim());
            if (shouldBackfill && value) {
                mergedInitialValues[key] = value;
            }
        });

        setOrderInitialValues({ ...mergedInitialValues, __timestamp: Date.now() });
        setShowOrderModal(true);
    };

    const handleOrderModalClose = () => {
        setShowOrderModal(false);
        refreshDraftState();
        fetchUserAndOrders(); // Refresh orders so any newly-created order shows up with Retry Payment
    };

    const handleResumeDraft = () => {
        if (!localDraft) return;
        handleOrderModalOpen({
            productType: localDraft.selectedPlan || 'Standard',
            quantity: localDraft.quantity || 1,
            selectedCountry: localDraft.selectedCountry || '',
            selectedState: localDraft.selectedState || '',
            selectedCity: localDraft.selectedCity || '',
            postalCode: localDraft.postalCode || '',
            phoneCountryCode: localDraft.phoneCountryCode || 'IN',
            organization: localDraft.organization || '',
            details: localDraft.details || '',
            savedAt: localDraft.savedAt || null,
            source: 'draft',
        });
    };

    const handleDeleteDraft = () => {
        clearOrderDraft();
        refreshDraftState();
        setOrderAction({ type: 'success', text: 'Draft removed.' });
    };

    const scrollToPlanControls = () => {
        if (typeof document === 'undefined') return;
        document.getElementById('workspace-plan-controls')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const getPlanActionLabel = (targetPlanKey) => {
        const targetMeta = PLAN_CATALOG[targetPlanKey];
        if (!targetMeta) return 'Choose plan';
        if (!hasActiveSubscription) return `Choose ${targetMeta.name}`;
        if (targetPlanKey === activePlanKey) return 'Current plan';
        return getPlanRank(targetPlanKey) > getPlanRank(activePlanKey)
            ? `Upgrade to ${targetMeta.name}`
            : `Downgrade to ${targetMeta.name}`;
    };

    const handleManagePlan = (targetPlanKey) => {
        const targetMeta = PLAN_CATALOG[targetPlanKey];
        if (!targetMeta) return;

        if (hasActiveSubscription) {
            if (targetPlanKey === activePlanKey) {
                setOrderAction({
                    type: 'success',
                    text: `${targetMeta.name} is already the active workspace plan.`,
                });
                return;
            }

            setOrderAction(null);
            handleOrderModalOpen({
                productType: targetMeta.name,
                quantity: 1,
                mode: 'plan_change',
                planChangeForOrder: activeSubscriptionOrder?.id,
                currentPlanName: currentPlanMeta.name,
                pendingPlanName,
                pendingChangeEffectiveAt: pendingPlanEffectiveAt,
            });
            return;
        }

        setOrderAction(null);
        handleOrderModalOpen({
            productType: targetMeta.name,
            quantity: 1,
        });
    };

    const styles = {
        container: {
            // styles handled by .dashboard-container class
        },
        header: {
            // styles handled by .dashboard-header class
        },
        verifyBanner: {
            background: '#FEF3C7',
            border: '1px solid #FCD34D',
            color: '#92400E',
            padding: '16px',
            borderRadius: '12px',
            margin: '0 32px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
        },
        verifyNowButton: {
            background: '#EA580C',
            color: '#fff',
            border: 'none',
            borderRadius: '999px',
            padding: '10px 20px',
            fontWeight: '600',
            cursor: 'pointer',
        },
        headerInner: {
            // styles handled by .dashboard-header-inner class
        },
        welcome: {
            fontSize: isSmallMobile ? '22px' : isMobile ? '24px' : isTabletOrBelow ? '26px' : '28px',
            fontWeight: '700',
            color: '#2B2B2B',
            marginBottom: '4px',
            lineHeight: 1.15,
        },
        subtitle: {
            fontSize: isMobile ? '14px' : '15px',
            color: '#6E6E6E',
            lineHeight: 1.5,
        },
        userInfo: {
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
        },
        logoutButton: {
            padding: '10px 24px',
            background: '#D81F26',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '999px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s',
        },
        main: {
            // styles handled by .dashboard-main class
        },
        dashboardShell: {
            display: 'grid',
            gridTemplateColumns: isTabletOrBelow ? '1fr' : isTabletLandscape ? '240px minmax(0, 1fr)' : '280px minmax(0, 1fr)',
            gap: isMobile ? '16px' : isTabletOrBelow ? '18px' : '24px',
            alignItems: 'start',
        },
        dashboardSidebar: {
            background: '#FFFFFF',
            border: '1px solid #E2E8F0',
            borderRadius: '18px',
            boxShadow: '0 18px 48px rgba(15, 23, 42, 0.06)',
            padding: isSmallMobile ? '14px' : isMobile ? '16px' : '20px',
            position: isTabletOrBelow ? 'static' : 'sticky',
            top: isTabletOrBelow ? 'auto' : '96px',
        },
        dashboardSidebarHeader: {
            marginBottom: '18px',
        },
        dashboardSidebarTitle: {
            margin: 0,
            fontSize: '18px',
            fontWeight: 800,
            color: '#0F172A',
        },
        dashboardSidebarText: {
            margin: '8px 0 0',
            fontSize: '13px',
            lineHeight: 1.6,
            color: '#64748B',
        },
        dashboardSidebarNav: {
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? '8px' : '10px',
        },
        dashboardNavButton: {
            width: '100%',
            borderRadius: '14px',
            border: '1px solid #E2E8F0',
            background: '#FFFFFF',
            padding: isSmallMobile ? '12px' : isMobile ? '13px 14px' : '14px 16px',
            textAlign: 'left',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
        },
        dashboardNavButtonActive: {
            background: '#EFF6FF',
            borderColor: '#BFDBFE',
            boxShadow: '0 8px 20px rgba(52, 87, 213, 0.08)',
        },
        dashboardNavLabel: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'flex-start' : 'center',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: isMobile ? '8px' : '12px',
            fontSize: '15px',
            fontWeight: 800,
            color: '#0F172A',
            marginBottom: '6px',
        },
        dashboardNavMeta: {
            fontSize: '12px',
            lineHeight: 1.5,
            color: '#64748B',
        },
        dashboardNavBadge: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '999px',
            background: '#E2E8F0',
            color: '#334155',
            fontSize: isSmallMobile ? '10px' : '11px',
            fontWeight: 800,
            padding: isMobile ? '5px 10px' : '4px 8px',
            minWidth: isMobile ? '0' : '48px',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            alignSelf: isMobile ? 'flex-start' : 'center',
        },
        dashboardContentArea: {
            minWidth: 0,
        },
        mobileSectionSwitcher: {
            display: 'grid',
            gap: '12px',
            marginBottom: isMobile ? '16px' : '18px',
            padding: isSmallMobile ? '14px' : '16px',
            background: '#FFFFFF',
            border: '1px solid #E2E8F0',
            borderRadius: '18px',
            boxShadow: '0 18px 48px rgba(15, 23, 42, 0.06)',
        },
        mobileSectionHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '12px',
        },
        mobileSectionEyebrow: {
            fontSize: '11px',
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: '#64748B',
        },
        mobileSectionCaption: {
            marginTop: '6px',
            fontSize: '13px',
            lineHeight: 1.55,
            color: '#64748B',
        },
        mobileSectionBadge: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '999px',
            background: '#E2E8F0',
            color: '#334155',
            fontSize: '11px',
            fontWeight: 800,
            padding: '6px 10px',
            whiteSpace: 'nowrap',
            flexShrink: 0,
        },
        mobileSectionSelect: {
            width: '100%',
            borderRadius: '14px',
            border: '1px solid #CBD5E1',
            background: '#F8FAFC',
            color: '#0F172A',
            fontSize: '15px',
            fontWeight: 700,
            padding: '14px 16px',
            outline: 'none',
        },
        dashboardPageHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: isMobile ? '12px' : '18px',
            marginBottom: isMobile ? '18px' : '22px',
            flexWrap: 'wrap',
            flexDirection: isTabletOrBelow ? 'column' : 'row',
        },
        dashboardPageTitle: {
            margin: 0,
            fontSize: isSmallMobile ? '20px' : isMobile ? '22px' : isTabletOrBelow ? '24px' : '28px',
            fontWeight: 800,
            color: '#0F172A',
            lineHeight: 1.15,
        },
        dashboardPageText: {
            margin: '8px 0 0',
            fontSize: isSmallMobile ? '13px' : isMobile ? '14px' : '15px',
            lineHeight: 1.6,
            color: '#64748B',
            maxWidth: '760px',
        },
        dashboardPageActions: {
            display: 'flex',
            gap: isMobile ? '10px' : '12px',
            flexWrap: 'wrap',
            width: isTabletOrBelow ? '100%' : 'auto',
            flexDirection: isSmallMobile ? 'column' : 'row',
        },
        sectionPanel: {
            background: '#FFFFFF',
            border: '1px solid #E2E8F0',
            borderRadius: '18px',
            padding: isSmallMobile ? '16px' : isMobile ? '18px' : '24px',
            boxShadow: '0 18px 48px rgba(15, 23, 42, 0.06)',
        },
        sectionSubheading: {
            margin: '0 0 18px',
            fontSize: '18px',
            fontWeight: 800,
            color: '#0F172A',
        },
        sectionDescription: {
            margin: '8px 0 0',
            fontSize: '14px',
            lineHeight: 1.6,
            color: '#64748B',
        },
        summaryGrid: {
            display: 'grid',
            gridTemplateColumns: isTabletOrBelow ? '1fr' : 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: isMobile ? '14px' : '18px',
        },
        detailGrid: {
            display: 'grid',
            gridTemplateColumns: isTabletOrBelow ? '1fr' : 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: isMobile ? '14px' : '16px',
        },
        detailCard: {
            border: '1px solid #E2E8F0',
            borderRadius: '14px',
            padding: '16px',
            background: '#FFFFFF',
        },
        detailLabel: {
            fontSize: '11px',
            color: '#94A3B8',
            marginBottom: '6px',
            textTransform: 'uppercase',
            fontWeight: 700,
            letterSpacing: '0.08em',
        },
        detailValue: {
            fontSize: '14px',
            lineHeight: 1.6,
            color: '#0F172A',
            fontWeight: 600,
        },
        inlineActionRow: {
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
            marginTop: '18px',
        },
        securityGrid: {
            display: 'grid',
            gridTemplateColumns: isTabletOrBelow ? '1fr' : 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: isMobile ? '14px' : '18px',
        },
        securityCard: {
            border: '1px solid #E2E8F0',
            borderRadius: '16px',
            padding: '18px',
            background: '#FFFFFF',
        },
        securityTitle: {
            margin: 0,
            fontSize: '16px',
            fontWeight: 800,
            color: '#0F172A',
        },
        securityText: {
            margin: '10px 0 0',
            fontSize: '14px',
            lineHeight: 1.6,
            color: '#64748B',
        },
        supportGrid: {
            display: 'grid',
            gridTemplateColumns: isTabletOrBelow ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: isMobile ? '14px' : '18px',
        },
        supportCard: {
            border: '1px solid #E2E8F0',
            borderRadius: '16px',
            padding: '20px',
            background: '#FFFFFF',
        },
        supportTitle: {
            margin: 0,
            fontSize: '16px',
            fontWeight: 800,
            color: '#0F172A',
        },
        supportText: {
            margin: '10px 0 0',
            fontSize: '14px',
            lineHeight: 1.7,
            color: '#475569',
        },
        supportInboxGrid: {
            display: 'grid',
            gridTemplateColumns: isTabletOrBelow ? '1fr' : 'minmax(230px, 0.36fr) minmax(0, 1fr)',
            gap: isMobile ? '14px' : '18px',
            marginTop: '18px',
        },
        supportThreadList: {
            display: 'grid',
            alignContent: 'start',
            gap: '10px',
            maxHeight: isMobile ? 'none' : '560px',
            overflowY: isMobile ? 'visible' : 'auto',
        },
        supportThreadButton: {
            width: '100%',
            border: '1px solid #E2E8F0',
            borderRadius: '14px',
            background: '#FFFFFF',
            padding: '14px',
            textAlign: 'left',
            cursor: 'pointer',
        },
        supportThreadButtonActive: {
            borderColor: '#3457D5',
            background: '#EFF6FF',
            boxShadow: '0 12px 24px rgba(52, 87, 213, 0.08)',
        },
        supportThreadTitle: {
            display: 'block',
            color: '#0F172A',
            fontSize: '14px',
            fontWeight: 800,
            lineHeight: 1.35,
            overflowWrap: 'anywhere',
        },
        supportThreadMeta: {
            display: 'block',
            color: '#64748B',
            fontSize: '12px',
            lineHeight: 1.45,
            marginTop: '6px',
        },
        supportConversationPanel: {
            border: '1px solid #E2E8F0',
            borderRadius: '16px',
            background: '#FFFFFF',
            overflow: 'hidden',
            minWidth: 0,
        },
        supportConversationHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            gap: '12px',
            alignItems: 'flex-start',
            padding: '16px',
            borderBottom: '1px solid #E2E8F0',
            background: '#F8FAFC',
        },
        supportStatusPill: {
            display: 'inline-flex',
            alignItems: 'center',
            minHeight: '26px',
            padding: '4px 10px',
            borderRadius: '999px',
            background: '#E4ECFF',
            color: '#3457D5',
            fontSize: '12px',
            fontWeight: 800,
            whiteSpace: 'nowrap',
        },
        supportMessageList: {
            display: 'grid',
            gap: '12px',
            padding: '16px',
            height: isMobile ? '360px' : '420px',
            overflowY: 'auto',
            background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
        },
        supportBubble: {
            maxWidth: isMobile ? '92%' : '78%',
            borderRadius: '16px',
            padding: '12px 14px',
            boxShadow: '0 10px 24px rgba(15, 23, 42, 0.05)',
            overflowWrap: 'anywhere',
        },
        supportBubbleInbound: {
            justifySelf: 'start',
            background: '#FFFFFF',
            border: '1px solid #E2E8F0',
            color: '#0F172A',
        },
        supportBubbleOutbound: {
            justifySelf: 'end',
            background: '#E4ECFF',
            border: '1px solid #BFDBFE',
            color: '#172554',
        },
        supportBubbleMeta: {
            display: 'block',
            marginBottom: '6px',
            color: '#64748B',
            fontSize: '11px',
            fontWeight: 800,
            textTransform: 'capitalize',
        },
        supportBubbleBody: {
            display: 'block',
            whiteSpace: 'pre-wrap',
            fontSize: '14px',
            lineHeight: 1.55,
        },
        supportComposer: {
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) auto',
            gap: '10px',
            padding: '14px',
            borderTop: '1px solid #E2E8F0',
            background: '#FFFFFF',
        },
        supportComposerInput: {
            width: '100%',
            minHeight: '50px',
            resize: 'vertical',
            border: '1px solid #CBD5E1',
            borderRadius: '14px',
            padding: '12px 14px',
            fontSize: '14px',
            lineHeight: 1.4,
            outline: 'none',
            boxSizing: 'border-box',
        },
        supportComposerButton: {
            width: '50px',
            height: '50px',
            border: 'none',
            borderRadius: '14px',
            background: '#3457D5',
            color: '#FFFFFF',
            fontSize: '22px',
            fontWeight: 900,
            cursor: 'pointer',
        },
        supportEmptyState: {
            display: 'grid',
            gap: '6px',
            padding: '16px',
            border: '1px dashed #CBD5E1',
            borderRadius: '14px',
            color: '#64748B',
            fontSize: '14px',
            lineHeight: 1.5,
        },
        linkAction: {
            color: '#3457D5',
            fontWeight: 700,
            textDecoration: 'none',
        },
        statsGrid: {
            // styles handled by .stats-grid class
        },
        statCard: {
            background: '#FFFFFF',
            padding: isSmallMobile ? '16px' : isMobile ? '18px' : '22px',
            borderRadius: '14px',
            border: '1px solid #E2E8F0',
            boxShadow: '0 18px 48px rgba(15, 23, 42, 0.06)',
            transition: 'box-shadow 0.2s',
        },
        dashboardHero: {
            display: 'grid',
            gridTemplateColumns: isTabletOrBelow ? '1fr' : 'minmax(0, 1.7fr) minmax(280px, 0.8fr)',
            gap: isMobile ? '14px' : '18px',
            marginBottom: isMobile ? '18px' : '22px',
        },
        heroPanel: {
            background: 'linear-gradient(135deg, #3457D5 0%, #203A9A 100%)',
            color: '#FFFFFF',
            borderRadius: '18px',
            padding: isSmallMobile ? '18px' : isMobile ? '20px' : '28px',
            boxShadow: '0 24px 64px rgba(52, 87, 213, 0.22)',
        },
        heroEyebrow: {
            margin: '0 0 10px',
            fontSize: '12px',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: '#FFE3CC',
            fontWeight: 700,
        },
        heroTitle: {
            margin: 0,
            fontSize: isSmallMobile ? '24px' : isMobile ? '26px' : isTabletOrBelow ? '28px' : '30px',
            lineHeight: 1.15,
            fontWeight: 800,
            color: '#FFFFFF',
        },
        heroText: {
            margin: '12px 0 0',
            color: '#CBD5E1',
            fontSize: isSmallMobile ? '13px' : isMobile ? '14px' : '15px',
            lineHeight: 1.6,
            maxWidth: '720px',
        },
        heroActions: {
            display: 'flex',
            gap: isMobile ? '10px' : '12px',
            flexWrap: 'wrap',
            marginTop: '22px',
            flexDirection: isSmallMobile ? 'column' : 'row',
        },
        outlineAction: {
            background: 'rgba(255,255,255,0.08)',
            color: '#FFFFFF',
            border: '1px solid rgba(255,255,255,0.18)',
        },
        heroPrimaryAction: {
            background: '#FFFFFF',
            color: '#3457D5',
            border: '1px solid rgba(255,255,255,0.2)',
        },
        nextStepPanel: {
            background: '#FFFFFF',
            border: '1px solid #E2E8F0',
            borderRadius: '18px',
            padding: isSmallMobile ? '16px' : isMobile ? '18px' : '24px',
            boxShadow: '0 18px 48px rgba(15, 23, 42, 0.06)',
        },
        statHelp: {
            color: '#64748B',
            fontSize: '13px',
            lineHeight: 1.5,
            marginTop: '8px',
        },
        readinessList: {
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            marginTop: '16px',
        },
        readinessItem: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            padding: '10px 12px',
            borderRadius: '10px',
            background: '#F8FAFC',
            border: '1px solid #E2E8F0',
            color: '#334155',
            fontSize: '13px',
            fontWeight: 700,
        },
        readinessMissing: {
            background: '#FFF7ED',
            border: '1px solid #FED7AA',
            color: '#9A3412',
        },
        progressTrack: {
            width: '100%',
            height: '8px',
            borderRadius: '999px',
            background: '#E2E8F0',
            overflow: 'hidden',
            marginTop: '14px',
        },
        progressFill: {
            height: '100%',
            borderRadius: '999px',
            background: 'linear-gradient(90deg, #3457D5 0%, #F28C46 100%)',
        },
        statLabel: {
            fontSize: '12px',
            color: '#9CA3AF',
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            fontWeight: '600',
        },
        statValue: {
            fontSize: isSmallMobile ? '26px' : isMobile ? '28px' : '32px',
            fontWeight: '700',
            color: '#0F172A',
        },
        section: {
            marginBottom: '32px',
        },
        sectionTitle: {
            fontSize: '20px',
            fontWeight: '700',
            color: '#0F172A',
            marginBottom: '16px',
        },
        ordersTable: {
            // styles handled by .orders-table-wrapper class
        },
        table: {
            // styles handled by .orders-table class
        },
        invoiceEmpty: {
            padding: '24px',
            textAlign: 'center',
            color: '#6B7280',
        },
        invoiceActions: {
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
        },
        invoiceActionButton: {
            padding: '6px 14px',
            borderRadius: '999px',
            border: '1px solid #D1D5DB',
            background: '#FFFFFF',
            color: '#111827',
            fontSize: '12px',
            fontWeight: '600',
            textDecoration: 'none',
        },
        th: {
            background: '#F9F9F9',
            padding: '16px',
            textAlign: 'left',
            fontSize: '13px',
            fontWeight: '600',
            color: '#6E6E6E',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            borderBottom: '1px solid #E5E5E5',
        },
        td: {
            padding: '16px',
            fontSize: '14px',
            color: '#414141',
            borderBottom: '1px solid #F9F9F9',
        },
        statusBadge: {
            padding: '4px 12px',
            borderRadius: '999px',
            fontSize: '12px',
            fontWeight: '600',
            display: 'inline-block',
        },
        pendingBadge: {
            background: '#FFE3CC',
            color: '#F28C46',
        },
        completedBadge: {
            background: '#E4ECFF',
            color: '#3457D5',
        },
        cancelledBadge: {
            background: '#F3F4F6',
            color: '#4B5563',
        },
        // Modal Styles
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: isSmallMobile ? '10px' : isMobile ? '14px' : '20px',
        },
        modal: {
            background: '#FFFFFF',
            width: '100%',
            maxWidth: isTabletOrBelow ? '100%' : '600px',
            borderRadius: isMobile ? '18px' : '24px',
            padding: isSmallMobile ? '16px' : isMobile ? '20px' : '32px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            maxHeight: '90vh',
            overflowY: 'auto',
        },
        modalTitle: {
            fontSize: '24px',
            fontWeight: '700',
            marginBottom: '8px',
            color: '#2B2B2B',
        },
        modalSub: {
            fontSize: '15px',
            color: '#6E6E6E',
            marginBottom: '24px',
        },
        formGroup: {
            marginBottom: '20px',
        },
        phoneRow: {
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            flexDirection: isMobile ? 'column' : 'row',
        },
        phoneBadge: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
        },
        unverifiedTag: {
            fontSize: '11px',
            padding: '2px 8px',
            borderRadius: '999px',
            background: '#FEE2E2',
            color: '#B42318',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
        },
        verifyInlineBtn: {
            border: 'none',
            background: 'transparent',
            color: '#3457D5',
            fontSize: '12px',
            cursor: 'pointer',
            textDecoration: 'underline',
        },
        label: {
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#414141',
            marginBottom: '8px',
        },
        input: {
            width: '100%',
            padding: '12px 16px',
            borderRadius: '12px',
            border: '1px solid #E5E5E5',
            fontSize: '15px',
            outline: 'none',
        },
        select: {
            width: '100%',
            padding: '12px 16px',
            borderRadius: '12px',
            border: '1px solid #E5E5E5',
            fontSize: '15px',
            outline: 'none',
            background: '#fff',
            cursor: 'pointer',
        },
        submitButton: {
            width: '100%',
            padding: '16px',
            background: '#3457D5',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s',
        },
        sectionHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
        },
        verifyBtn: {
            fontSize: '12px',
            padding: '4px 8px',
            border: '1px solid #E5E5E5',
            borderRadius: '4px',
            background: '#fff',
            cursor: 'pointer',
            marginLeft: '8px',
            color: '#3457D5',
        },
        verifiedTag: {
            background: '#DCFCE7',
            color: '#166534',
            fontSize: '11px',
            fontWeight: '600',
            padding: '2px 10px',
            borderRadius: '999px',
        },
        inlineHelper: {
            marginTop: '6px',
            fontSize: '12px',
        },
        inlineHelperInfo: {
            color: '#1D4ED8',
        },
        inlineHelperError: {
            color: '#B42318',
        },
        inlineHelperSuccess: {
            color: '#15803D',
        },
        phoneVerifyActions: {
            display: 'flex',
            gap: '12px',
            marginTop: '16px',
            flexWrap: 'wrap',
        },
        phoneVerifyButton: {
            padding: '12px 18px',
            borderRadius: '10px',
            border: 'none',
            cursor: 'pointer',
            fontWeight: '600',
        },
        secondaryButton: {
            background: '#E5E7EB',
            color: '#111827',
        },
        primaryButton: {
            background: '#3457D5',
            color: '#FFFFFF',
        },
        codeInput: {
            width: '100%',
            padding: '12px 16px',
            borderRadius: '10px',
            border: '1px solid #D1D5DB',
            fontSize: '16px',
            textAlign: 'center',
            letterSpacing: '0.3em',
        },
        // Action Buttons
        actionButton: {
            padding: isSmallMobile ? '10px 14px' : '10px 20px',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.3s',
            width: isSmallMobile ? '100%' : 'auto',
            minWidth: 0,
            textAlign: 'center',
        },
        primaryAction: {
            background: '#3457D5',
            color: '#fff',
        },
        secondaryAction: {
            background: '#F5F5F7',
            color: '#414141',
        },
        alert: {
            padding: '14px 16px',
            borderRadius: '12px',
            marginBottom: '16px',
            fontSize: '14px',
            fontWeight: '600',
        },
        alertSuccess: {
            background: '#E6F4EA',
            color: '#1E7F2C',
            border: '1px solid #B5E0C1',
        },
        alertError: {
            background: '#FFE0E0',
            color: '#B42318',
            border: '1px solid #F5B5B5',
        },
        alertInfo: {
            background: '#EFF6FF',
            color: '#1E3A8A',
            border: '1px solid #BFDBFE',
        },
        retryButton: {
            padding: '8px 18px',
            borderRadius: '999px',
            border: 'none',
            background: '#3457D5',
            color: '#FFFFFF',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'opacity 0.2s',
        },
        retryButtonDisabled: {
            opacity: 0.6,
            cursor: 'not-allowed',
        },
        deleteButton: {
            padding: '8px 18px',
            borderRadius: '999px',
            border: 'none',
            background: '#FEE2E2',
            color: '#B42318',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'opacity 0.2s',
        },
        cancelSubscriptionButton: {
            padding: '8px 18px',
            borderRadius: '999px',
            border: '1px solid #D1D5DB',
            background: '#FFFFFF',
            color: '#374151',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'opacity 0.2s',
        },
        closeButton: {
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'transparent',
            border: 'none',
            fontSize: '24px',
            lineHeight: '1',
            color: '#9CA3AF',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
        },
    };

    const currentSection = dashboardSections.find((section) => section.id === activeView) || dashboardSections[0];

    const renderPageActions = () => {
        switch (activeView) {
            case 'overview':
                return (
                    <>
                        <button
                            type="button"
                            style={{ ...styles.actionButton, ...styles.primaryAction }}
                            onClick={() => setActiveView('billing')}
                        >
                            {hasPlan ? 'Manage billing' : 'Choose plan'}
                        </button>
                        <button
                            type="button"
                            style={{ ...styles.actionButton, ...styles.secondaryAction }}
                            onClick={() => setActiveView('settings')}
                        >
                            Workspace settings
                        </button>
                    </>
                );
            case 'billing':
                return (
                    <>
                        <button
                            type="button"
                            style={{ ...styles.actionButton, ...styles.primaryAction }}
                            onClick={scrollToPlanControls}
                        >
                            {hasPlan ? 'Review plan options' : 'Choose plan'}
                        </button>
                        {canCancelCurrentSubscription && (
                            <button
                                type="button"
                                style={{
                                    ...styles.actionButton,
                                    ...styles.secondaryAction,
                                    opacity: cancelingSubscriptionId === activeSubscriptionOrder?.subscription_id ? 0.6 : 1,
                                }}
                                disabled={cancelingSubscriptionId === activeSubscriptionOrder?.subscription_id}
                                onClick={() => handleCancelSubscription(activeSubscriptionOrder)}
                            >
                                {cancelingSubscriptionId === activeSubscriptionOrder?.subscription_id ? 'Cancelling…' : 'Cancel auto-renew'}
                            </button>
                        )}
                        <button
                            type="button"
                            style={{ ...styles.actionButton, ...styles.secondaryAction }}
                            onClick={handleEditProfile}
                        >
                            Update billing info
                        </button>
                    </>
                );
            case 'security':
                return (
                    <>
                        <button
                            type="button"
                            style={{ ...styles.actionButton, ...styles.primaryAction }}
                            onClick={() => setShowPasswordModal(true)}
                        >
                            Change password
                        </button>
                        {!isPhoneVerified && (
                            <button
                                type="button"
                                style={{ ...styles.actionButton, ...styles.secondaryAction }}
                                onClick={handleRequirePhoneVerification}
                            >
                                Verify phone
                            </button>
                        )}
                    </>
                );
            case 'settings':
            case 'team':
                return (
                    <button
                        type="button"
                        style={{ ...styles.actionButton, ...styles.primaryAction }}
                        onClick={handleEditProfile}
                    >
                        Edit workspace profile
                    </button>
                );
            case 'knowledge':
            case 'integrations':
                return (
                    <a href="/#contact" style={{ ...styles.actionButton, ...styles.primaryAction, textDecoration: 'none' }}>
                        Contact Corpusio
                    </a>
                );
            default:
                return null;
        }
    };

    const renderOrdersTable = () => (
        <div className="orders-table-wrapper" style={styles.ordersTable}>
            {orders.length > 0 ? (
                <table className="orders-table" style={styles.table}>
                    <thead>
                        <tr>
                            <th style={styles.th}>Order ID</th>
                            <th style={styles.th}>Product</th>
                            <th style={styles.th}>Quantity</th>
                            <th style={styles.th}>Date</th>
                            <th style={styles.th}>Status</th>
                            <th style={styles.th}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {localDraft && (
                            <tr key="local-draft">
                                <td style={styles.td}>—</td>
                                <td style={styles.td}>{localDraft.selectedPlan || 'Draft'}</td>
                                <td style={styles.td}>{localDraft.quantity || 1}</td>
                                <td style={styles.td}>
                                    {localDraft.savedAt ? new Date(localDraft.savedAt).toLocaleDateString() : 'Draft'}
                                </td>
                                <td style={styles.td}>
                                    <span style={{ ...styles.statusBadge, ...styles.pendingBadge }}>Draft</span>
                                </td>
                                <td style={styles.td}>
                                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                        <button type="button" style={styles.retryButton} onClick={handleResumeDraft}>
                                            Continue Draft
                                        </button>
                                        <button type="button" style={styles.deleteButton} onClick={handleDeleteDraft}>
                                            Delete
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )}
                        {orders.map((order) => {
                            const normalizedStatus = (order.status || '').toLowerCase();
                            const isSubscriptionCancelled = String(order.subscription_status || '').toLowerCase() === 'cancelled';
                            const canRetry = ['created', 'failed', 'pending', 'reauthorization_required'].includes(normalizedStatus) && !isSubscriptionCancelled;
                            const linkedInvoice = invoiceMap[order.id];

                            return (
                                <tr key={order.id}>
                                    <td style={styles.td}>{order.id}</td>
                                    <td style={styles.td}>{order.product_name}</td>
                                    <td style={styles.td}>{order.quantity}</td>
                                    <td style={styles.td}>{new Date(order.created_at).toLocaleDateString()}</td>
                                    <td style={styles.td}>
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                            <span
                                                style={{
                                                    ...styles.statusBadge,
                                                    ...(normalizedStatus === 'pending' || normalizedStatus === 'reauthorization_required'
                                                        ? styles.pendingBadge
                                                        : styles.completedBadge),
                                                }}
                                            >
                                                {order.status}
                                            </span>
                                            {isSubscriptionCancelled && (
                                                <span style={{ ...styles.statusBadge, ...styles.cancelledBadge }}>
                                                    Auto-renew off
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td style={styles.td}>
                                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                            {['created', 'failed', 'pending', 'draft', 'reauthorization_required'].includes(normalizedStatus) && (
                                                <>
                                                    {canRetry && (
                                                        <button
                                                            type="button"
                                                            style={{
                                                                ...styles.retryButton,
                                                                ...(retryingOrderId === order.id ? styles.retryButtonDisabled : {}),
                                                            }}
                                                            disabled={retryingOrderId === order.id}
                                                            onClick={() => handleRetryPayment(order)}
                                                        >
                                                            {retryingOrderId === order.id ? 'Retrying…' : 'Retry Payment'}
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        style={{ ...styles.deleteButton, opacity: deletingOrderId === order.id ? 0.6 : 1 }}
                                                        disabled={deletingOrderId === order.id}
                                                        onClick={() => handleDeleteOrder(order)}
                                                    >
                                                        {deletingOrderId === order.id ? 'Deleting…' : 'Delete'}
                                                    </button>
                                                </>
                                            )}

                                            {order.can_cancel_subscription && (
                                                <button
                                                    type="button"
                                                    style={{
                                                        ...styles.cancelSubscriptionButton,
                                                        opacity: cancelingSubscriptionId === order.subscription_id ? 0.6 : 1,
                                                    }}
                                                    disabled={cancelingSubscriptionId === order.subscription_id}
                                                    onClick={() => handleCancelSubscription(order)}
                                                >
                                                    {cancelingSubscriptionId === order.subscription_id ? 'Cancelling…' : 'Cancel Subscription'}
                                                </button>
                                            )}

                                            {normalizedStatus === 'superseded' && (
                                                <button
                                                    type="button"
                                                    style={{ ...styles.deleteButton, opacity: deletingOrderId === order.id ? 0.6 : 1 }}
                                                    disabled={deletingOrderId === order.id}
                                                    onClick={() => handleDeleteOrder(order)}
                                                >
                                                    {deletingOrderId === order.id ? 'Deleting…' : 'Delete'}
                                                </button>
                                            )}

                                            {linkedInvoice && (
                                                <>
                                                    <a
                                                        href={linkedInvoice.pdfsignedinline || linkedInvoice.pdfurlinline}
                                                        style={{ ...styles.actionButton, ...styles.secondaryAction, fontSize: '13px', padding: '6px 12px' }}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        View Invoice
                                                    </a>
                                                    <a
                                                        href={linkedInvoice.pdfsigneddownload || linkedInvoice.pdfurldownload}
                                                        style={{ ...styles.actionButton, ...styles.secondaryAction, fontSize: '13px', padding: '6px 12px' }}
                                                        download
                                                    >
                                                        Download
                                                    </a>
                                                </>
                                            )}

                                            {!['created', 'failed', 'pending', 'draft', 'superseded'].includes(normalizedStatus) && !linkedInvoice && (
                                                <span style={{ color: '#9CA3AF', fontSize: '13px' }}>—</span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            ) : (
                <div style={{ padding: '48px', textAlign: 'center', color: '#6E6E6E' }}>
                    <p>No billing history yet. Choose a plan to activate the workspace and start recurring billing.</p>
                    <a href="/#pricing" style={{ color: '#3457D5', fontWeight: '600', marginTop: '16px', display: 'inline-block' }}>
                        View Plans →
                    </a>
                </div>
            )}
        </div>
    );

    const renderOverviewContent = () => (
        <>
            <div className="dashboard-hero" style={styles.dashboardHero}>
                <section style={styles.heroPanel}>
                    <p style={styles.heroEyebrow}>Corpusio command center</p>
                    <h2 style={styles.heroTitle}>
                        {hasPlan ? `${currentPlanMeta.name} workspace, ${billingIntervalLabel} billing` : 'Get your workspace ready for launch'}
                    </h2>
                    <p style={styles.heroText}>
                        Run Corpusio like a proper SaaS workspace: track your current plan, recurring billing, payment readiness, and operating health from one command center.
                    </p>
                    <div style={styles.heroActions}>
                        <button
                            style={{ ...styles.actionButton, ...styles.heroPrimaryAction }}
                            onClick={() => setActiveView('billing')}
                        >
                            {hasPlan ? 'Manage billing' : 'Choose a plan'}
                        </button>
                        <button
                            style={{ ...styles.actionButton, ...styles.outlineAction }}
                            onClick={() => setActiveView('knowledge')}
                        >
                            Knowledge sources
                        </button>
                        <button
                            style={{ ...styles.actionButton, ...styles.outlineAction }}
                            onClick={() => setActiveView('team')}
                        >
                            Team & access
                        </button>
                    </div>
                </section>
                <aside style={styles.nextStepPanel}>
                    <div style={styles.statLabel}>Next best action</div>
                    <div style={{ fontSize: '20px', fontWeight: 800, color: '#0F172A', lineHeight: 1.3 }}>
                        {nextStep}
                    </div>
                    <div style={styles.statHelp}>{readinessSummary}</div>
                    <div style={styles.progressTrack}>
                        <div style={{ ...styles.progressFill, width: `${profileCompletion}%` }} />
                    </div>
                    <div style={{ marginTop: '14px', fontSize: '13px', color: '#475569', lineHeight: 1.6 }}>
                        {hasPlan
                            ? `Next renewal: ${formatDashboardDate(planRenewalDate)}`
                            : 'No active plan yet. Choose a subscription to begin recurring workspace billing.'}
                    </div>
                </aside>
            </div>

            <div className="stats-grid" style={styles.statsGrid}>
                <div style={styles.statCard}>
                    <div style={styles.statLabel}>Current plan</div>
                    <div style={styles.statValue}>{hasPlan ? currentPlanMeta.name : 'None'}</div>
                    <div style={styles.statHelp}>
                        {hasPlan
                            ? `${billingIntervalLabel.charAt(0).toUpperCase()}${billingIntervalLabel.slice(1)} renewal on ${formatDashboardDate(planRenewalDate)}`
                            : 'Set up your workspace subscription to activate recurring access and billing controls.'}
                    </div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statLabel}>Billing health</div>
                    <div style={styles.statValue}>{retryRequiredCount ? 'Attention' : 'Healthy'}</div>
                    <div style={styles.statHelp}>
                        {retryRequiredCount
                            ? `${retryRequiredCount} billing item${retryRequiredCount === 1 ? '' : 's'} need payment attention.`
                            : 'No recurring payment issues detected right now.'}
                    </div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statLabel}>Knowledge readiness</div>
                    <div style={styles.statValue}>{profileCompletion}%</div>
                    <div style={styles.statHelp}>
                        Billing and workspace identity checks are {profileCompletion}% complete.
                    </div>
                    <div style={styles.progressTrack}>
                        <div style={{ ...styles.progressFill, width: `${profileCompletion}%` }} />
                    </div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statLabel}>Invoices</div>
                    <div style={styles.statValue}>{invoiceCount}</div>
                    <div style={styles.statHelp}>
                        {latestInvoice
                            ? `Latest invoice ready for ${formatDashboardDate(latestInvoice.createdat || latestInvoice.created_at || latestInvoice.timestamp)}`
                            : 'Invoices will appear here once your workspace bills successfully.'}
                    </div>
                </div>
            </div>

            <div style={{ display: 'grid', gap: isMobile ? '14px' : '18px', gridTemplateColumns: isTabletOrBelow ? '1fr' : '1.4fr 1fr' }}>
                <div style={styles.sectionPanel}>
                    <h3 style={styles.sectionSubheading}>Workspace command summary</h3>
                    <p style={styles.sectionDescription}>
                        The high-signal operating snapshot for your subscription, default payment method, billing contact, and workspace configuration.
                    </p>
                    <div style={{ ...styles.detailGrid, marginTop: '18px' }}>
                        <div style={styles.detailCard}>
                            <div style={styles.detailLabel}>Subscription status</div>
                            <div style={styles.detailValue}>{hasPlan ? (planStatus || 'active') : 'No active plan'}</div>
                        </div>
                        <div style={styles.detailCard}>
                            <div style={styles.detailLabel}>Default payment method</div>
                            <div style={styles.detailValue}>{paymentMethodSummary}</div>
                            <div style={styles.statHelp}>{savedPaymentHint}</div>
                        </div>
                        <div style={styles.detailCard}>
                            <div style={styles.detailLabel}>Workspace owner</div>
                            <div style={styles.detailValue}>{user.name || user.email}</div>
                        </div>
                        <div style={styles.detailCard}>
                            <div style={styles.detailLabel}>Workspace location</div>
                            <div style={styles.detailValue}>{workspaceLocation}</div>
                        </div>
                    </div>
                </div>

                <div style={styles.sectionPanel}>
                    <h3 style={styles.sectionSubheading}>Recommended actions</h3>
                    <div style={{ ...styles.readinessList, marginTop: '16px' }}>
                        <div style={{ ...styles.readinessItem, ...(needsEmailVerification ? styles.readinessMissing : {}) }}>
                            <span>Email verification</span>
                            <span>{needsEmailVerification ? 'Send link' : 'Complete'}</span>
                        </div>
                        <div style={{ ...styles.readinessItem, ...(!isPhoneVerified ? styles.readinessMissing : {}) }}>
                            <span>Phone verification</span>
                            <span>{!isPhoneVerified ? 'Verify now' : 'Complete'}</span>
                        </div>
                        <div style={{ ...styles.readinessItem, ...(retryRequiredCount ? styles.readinessMissing : {}) }}>
                            <span>Billing operations</span>
                            <span>{retryRequiredCount ? 'Resolve payment' : 'Clear'}</span>
                        </div>
                        <div style={{ ...styles.readinessItem, ...(missingReadinessItems.length ? styles.readinessMissing : {}) }}>
                            <span>Workspace profile</span>
                            <span>{missingReadinessItems.length ? `${missingReadinessItems.length} item(s)` : 'Complete'}</span>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    const renderKnowledgeSourcesContent = () => (
        <div style={{ display: 'grid', gap: '18px' }}>
            <div style={styles.sectionPanel}>
                <h3 style={styles.sectionSubheading}>Knowledge source rollout</h3>
                <p style={styles.sectionDescription}>
                    Corpusio’s workspace model is built around connected knowledge. This page is where connected drives, websites, PDFs, and enterprise repositories belong.
                </p>
                <div style={{ ...styles.detailGrid, marginTop: '18px' }}>
                    {[
                        ['Document uploads', 'Manual upload intake for PDFs and internal documents', 'Ready for enablement'],
                        ['Website crawl', 'Sync public or authenticated website content into the workspace', 'Setup required'],
                        ['Google Drive / SharePoint', 'Enterprise source connectors for ongoing sync and indexing', 'Contact support'],
                        ['Index health', 'Failed syncs, stale documents, and freshness checks will appear here', 'No sources yet'],
                    ].map(([label, description, status]) => (
                        <div key={label} style={styles.detailCard}>
                            <div style={styles.detailLabel}>{label}</div>
                            <div style={styles.detailValue}>{status}</div>
                            <div style={styles.statHelp}>{description}</div>
                        </div>
                    ))}
                </div>
                <div style={styles.inlineActionRow}>
                    <a href="/#contact" style={styles.linkAction}>Talk to Corpusio about connector setup</a>
                </div>
            </div>
        </div>
    );

    const renderAgentsContent = () => (
        <div style={{ display: 'grid', gap: '18px' }}>
            <div style={styles.sectionPanel}>
                <h3 style={styles.sectionSubheading}>AI workflows and agents</h3>
                <p style={styles.sectionDescription}>
                    As Corpusio shifts into full SaaS mode, this area becomes the control layer for assistants, retrieval workflows, and business automations.
                </p>
                <div style={{ ...styles.detailGrid, marginTop: '18px' }}>
                    {[
                        ['Workspace assistant', 'Default answer assistant for search and document understanding', hasPlan ? 'Ready to configure' : 'Plan required'],
                        ['Escalation workflow', 'Route low-confidence answers to a human follow-up path', 'Drafting'],
                        ['Sales copilot', 'Answer customer and partner questions from approved content', 'Planned'],
                        ['Support copilot', 'Use connected knowledge to speed issue resolution', 'Planned'],
                    ].map(([label, description, status]) => (
                        <div key={label} style={styles.detailCard}>
                            <div style={styles.detailLabel}>{label}</div>
                            <div style={styles.detailValue}>{status}</div>
                            <div style={styles.statHelp}>{description}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderConversationsContent = () => {
        const selectedThread = supportThreads.find((item) => String(item.id) === String(supportSelectedId));
        return (
            <div style={{ display: 'grid', gap: '18px' }}>
                <div style={styles.sectionPanel}>
                    <div style={styles.dashboardPageHeader}>
                        <div>
                            <h3 style={styles.sectionSubheading}>Support conversations</h3>
                            <p style={styles.sectionDescription}>
                                Start a support thread from Corpusio, let the AI support layer gather context, and keep the handoff ready for a Kirusa operator when needed.
                            </p>
                        </div>
                        <button type="button" onClick={createSupportThread} disabled={supportLoading} style={{ ...styles.actionButton, ...styles.primaryAction }}>
                            {supportLoading ? 'Starting...' : 'New support chat'}
                        </button>
                    </div>
                    {supportError ? <div style={{ ...styles.alert, ...styles.alertError }}>{supportError}</div> : null}
                    <div style={styles.supportInboxGrid}>
                        <div style={styles.supportThreadList}>
                            {supportThreads.map((thread) => {
                                const active = String(thread.id) === String(supportSelectedId);
                                return (
                                    <button
                                        key={thread.id}
                                        type="button"
                                        onClick={() => setSupportSelectedId(thread.id)}
                                        style={{
                                            ...styles.supportThreadButton,
                                            ...(active ? styles.supportThreadButtonActive : {}),
                                        }}
                                    >
                                        <span style={styles.supportThreadTitle}>{thread.title || thread.workspace_name || `Thread ${thread.id}`}</span>
                                        <span style={styles.supportThreadMeta}>{thread.caseid || thread.status || 'Open'} · {thread.timestamp || ''}</span>
                                    </button>
                                );
                            })}
                            {!supportThreads.length && !supportLoading ? (
                                <div style={styles.supportEmptyState}>
                                    <strong>No support chats yet.</strong>
                                    <span>Open a chat when you need help with billing, onboarding, documents, or workspace setup.</span>
                                </div>
                            ) : null}
                        </div>
                        <div style={styles.supportConversationPanel}>
                            <div style={styles.supportConversationHeader}>
                                <div>
                                    <div style={styles.detailLabel}>Active thread</div>
                                    <strong>{selectedThread?.title || 'Select a support chat'}</strong>
                                </div>
                                <span style={styles.supportStatusPill}>{selectedThread?.status || 'Support'}</span>
                            </div>
                            <div style={styles.supportMessageList}>
                                {supportMessages.map((message) => {
                                    const outbound = String(message.sendertype || '').toLowerCase() !== 'user';
                                    return (
                                        <div
                                            key={message.id}
                                            style={{
                                                ...styles.supportBubble,
                                                ...(outbound ? styles.supportBubbleOutbound : styles.supportBubbleInbound),
                                            }}
                                        >
                                            <span style={styles.supportBubbleMeta}>{message.sendertype || 'message'} · {message.timestamp || ''}</span>
                                            <span style={styles.supportBubbleBody}>{message.message}</span>
                                        </div>
                                    );
                                })}
                                {!supportMessages.length ? (
                                    <div style={styles.supportEmptyState}>
                                        <strong>{supportSelectedId ? 'No replies yet.' : 'Choose or create a thread.'}</strong>
                                        <span>Messages will stay here so you and the Kirusa support team share one timeline.</span>
                                    </div>
                                ) : null}
                            </div>
                            <form onSubmit={sendSupportMessage} style={styles.supportComposer}>
                                <textarea
                                    value={supportDraft}
                                    onChange={(event) => setSupportDraft(event.target.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter' && !event.shiftKey) {
                                            event.preventDefault();
                                            event.currentTarget.form?.requestSubmit();
                                        }
                                    }}
                                    disabled={!supportSelectedId || supportSending}
                                    placeholder="Type your support message..."
                                    rows={2}
                                    style={styles.supportComposerInput}
                                />
                                <button type="submit" disabled={!supportDraft.trim() || !supportSelectedId || supportSending} style={styles.supportComposerButton}>
                                    →
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderAnalyticsContent = () => (
        <div style={{ display: 'grid', gap: '18px' }}>
            <div style={styles.summaryGrid}>
                <div style={styles.sectionPanel}>
                    <div style={styles.statLabel}>Workspace health</div>
                    <div style={styles.statValue}>{profileCompletion}%</div>
                    <div style={styles.statHelp}>Profile, verification, and billing readiness combined into one operational score.</div>
                </div>
                <div style={styles.sectionPanel}>
                    <div style={styles.statLabel}>Recurring billing</div>
                    <div style={styles.statValue}>{activeSubscriptions}</div>
                    <div style={styles.statHelp}>Active auto-renewing subscriptions currently attached to this workspace.</div>
                </div>
                <div style={styles.sectionPanel}>
                    <div style={styles.statLabel}>Invoice coverage</div>
                    <div style={styles.statValue}>{invoiceCount}</div>
                    <div style={styles.statHelp}>Successful billing events that already produced downloadable invoice records.</div>
                </div>
            </div>
            <div style={styles.sectionPanel}>
                <h3 style={styles.sectionSubheading}>Usage instrumentation</h3>
                <p style={styles.sectionDescription}>
                    Query volume, token consumption, document indexing, and answer-quality analytics should surface here next. For now, billing and readiness are the strongest available operational signals in this workspace.
                </p>
            </div>
        </div>
    );

    const renderBillingContent = () => (
        <div style={{ display: 'grid', gap: '18px' }}>
            <div style={styles.sectionPanel}>
                <div style={{ ...styles.dashboardPageHeader, marginBottom: '18px' }}>
                    <div>
                        <h3 style={styles.sectionSubheading}>Current subscription</h3>
                        <p style={styles.sectionDescription}>
                            One workspace, one plan. Upgrades charge immediately and reset the billing date to today; downgrades are scheduled for the next billing date while payment status comes from live backend billing.
                        </p>
                    </div>
                    <div style={styles.dashboardPageActions}>
                        <button
                            type="button"
                            style={{ ...styles.actionButton, ...styles.primaryAction }}
                            onClick={() => {
                                setOrderAction(null);
                                if (hasPlan) {
                                    handleOrderModalOpen({
                                        productType: currentPlanMeta.name,
                                        quantity: 1,
                                        mode: 'plan_change',
                                        planChangeForOrder: activeSubscriptionOrder?.id,
                                        currentPlanName: currentPlanMeta.name,
                                        pendingPlanName,
                                        pendingChangeEffectiveAt: pendingPlanEffectiveAt,
                                        source: 'billing_header',
                                    });
                                    return;
                                }
                                handleOrderModalOpen({
                                    productType: currentPlanMeta.name || 'Standard',
                                    quantity: 1,
                                    source: 'billing_header',
                                });
                            }}
                        >
                            {hasPlan ? 'Change plan' : 'Choose a plan'}
                        </button>
                        {canCancelCurrentSubscription && (
                            <button
                                type="button"
                                style={{
                                    ...styles.actionButton,
                                    ...styles.secondaryAction,
                                    opacity: cancelingSubscriptionId === activeSubscriptionOrder?.subscription_id ? 0.6 : 1,
                                }}
                                disabled={cancelingSubscriptionId === activeSubscriptionOrder?.subscription_id}
                                onClick={() => handleCancelSubscription(activeSubscriptionOrder)}
                            >
                                {cancelingSubscriptionId === activeSubscriptionOrder?.subscription_id ? 'Cancelling…' : 'Cancel auto-renew'}
                            </button>
                        )}
                    </div>
                </div>
                {hasPendingDowngrade && (
                    <div style={{ ...styles.alert, ...styles.alertInfo, marginBottom: '18px' }}>
                        A downgrade to <strong>{pendingPlanName}</strong> is scheduled for <strong>{formatDashboardDate(pendingPlanEffectiveAt)}</strong>. Your current plan remains active until then.
                    </div>
                )}
                <div style={{ ...styles.detailGrid, marginBottom: '18px' }}>
                    <div style={styles.detailCard}>
                        <div style={styles.detailLabel}>Plan</div>
                        <div style={styles.detailValue}>{hasPlan ? currentPlanMeta.name : 'No active subscription'}</div>
                        <div style={styles.statHelp}>{currentPlanMeta.summary}</div>
                    </div>
                    <div style={styles.detailCard}>
                        <div style={styles.detailLabel}>Renewal</div>
                        <div style={styles.detailValue}>{hasPlan ? formatDashboardDate(planRenewalDate) : 'Not scheduled'}</div>
                        <div style={styles.statHelp}>
                            {hasPendingDowngrade
                                ? `Current plan stays active until ${formatDashboardDate(pendingPlanEffectiveAt)}, when the scheduled downgrade takes effect.`
                                : planRenewalDueNow
                                    ? 'A renewal payment is scheduled today from backend billing.'
                                    : planRenewalDueSoon
                                        ? 'A renewal payment is scheduled within 24 hours; email and SMS reminders will be sent.'
                                : hasPlan
                                    ? `Auto-renews ${billingIntervalLabel}.`
                                    : 'Your next billing date appears here after activation.'}
                        </div>
                    </div>
                    <div style={styles.detailCard}>
                        <div style={styles.detailLabel}>Current billing amount</div>
                        <div style={styles.detailValue}>
                            {currentSubscriptionOrder?.subscription_amount_formatted || (currentSubscriptionOrder?.amount ? formatCurrencyAmount(currentSubscriptionOrder.amount, currentSubscriptionOrder.currency) : 'Pending first invoice')}
                        </div>
                        <div style={styles.statHelp}>This reflects the live subscription amount attached to the workspace plan.</div>
                    </div>
                    <div style={styles.detailCard}>
                        <div style={styles.detailLabel}>Payment status</div>
                        <div style={styles.detailValue}>{hasPlan ? (livePaymentStatus || 'active') : 'Not started'}</div>
                        <div style={styles.statHelp}>
                            {retryRequiredCount ? 'At least one billing item needs action before the workspace is fully clear.' : 'Recurring billing is currently healthy.'}
                        </div>
                    </div>
                </div>
            </div>

            <div id="workspace-plan-controls" style={styles.sectionPanel}>
                <h3 style={styles.sectionSubheading}>Upgrade or downgrade workspace plan</h3>
                <p style={styles.sectionDescription}>
                    Corpusio runs as one company workspace with one active plan at a time. Upgrades take effect immediately and restart billing from the upgrade date. Downgrades are queued for the next billing date, and any renewal payment due before then is shown from backend billing.
                </p>
                <div style={{ display: 'grid', gap: isMobile ? '14px' : '18px', gridTemplateColumns: isTabletOrBelow ? '1fr' : 'repeat(3, minmax(0, 1fr))', marginTop: '18px' }}>
                    {Object.values(PLAN_CATALOG).map((plan) => {
                        const isCurrent = hasActiveSubscription && plan.key === activePlanKey;
                        const actionLabel = getPlanActionLabel(plan.key);
                        return (
                            <div
                                key={plan.key}
                                style={{
                                    border: `1px solid ${isCurrent ? '#93C5FD' : '#E2E8F0'}`,
                                    background: isCurrent ? '#EFF6FF' : '#FFFFFF',
                                    borderRadius: '18px',
                                    padding: isSmallMobile ? '16px' : '18px',
                                    boxShadow: isCurrent ? '0 12px 28px rgba(52, 87, 213, 0.10)' : 'none',
                                    display: 'grid',
                                    gap: '14px',
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                                    <div>
                                        <div style={{ fontSize: '18px', fontWeight: 800, color: '#0F172A' }}>{plan.name}</div>
                                        <div style={{ marginTop: '6px', fontSize: '14px', lineHeight: 1.55, color: '#64748B' }}>{plan.summary}</div>
                                    </div>
                                    <span style={{ ...styles.mobileSectionBadge, background: isCurrent ? '#DBEAFE' : '#F1F5F9', color: isCurrent ? '#1D4ED8' : '#334155' }}>
                                        {isCurrent ? 'Current' : 'Available'}
                                    </span>
                                </div>
                                <div style={{ display: 'grid', gap: '10px' }}>
                                    {plan.features.map((feature) => (
                                        <div key={feature} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                            <span style={{ color: '#3457D5', fontWeight: 800 }}>•</span>
                                            <span style={{ fontSize: '14px', lineHeight: 1.55, color: '#334155' }}>{feature}</span>
                                        </div>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    style={{
                                        ...styles.actionButton,
                                        ...(isCurrent ? styles.secondaryAction : styles.primaryAction),
                                        width: '100%',
                                        opacity: isCurrent ? 0.9 : 1,
                                    }}
                                    onClick={() => handleManagePlan(plan.key)}
                                    disabled={isCurrent}
                                >
                                    {actionLabel}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div style={{ display: 'grid', gap: isMobile ? '14px' : '18px', gridTemplateColumns: isTabletOrBelow ? '1fr' : '1fr 1fr' }}>
                <div style={styles.sectionPanel}>
                    <h3 style={styles.sectionSubheading}>Plan includes</h3>
                    <p style={styles.sectionDescription}>
                        Keep the workspace aligned to the plan your company is paying for. Upgrade or downgrade from pricing whenever your usage pattern changes.
                    </p>
                    <div style={{ display: 'grid', gap: '12px', marginTop: '18px' }}>
                        {currentPlanFeatures.map((feature) => (
                            <div
                                key={feature}
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '10px',
                                    padding: '12px 14px',
                                    borderRadius: '14px',
                                    background: '#F8FAFC',
                                    border: '1px solid #E2E8F0',
                                }}
                            >
                                <span style={{ color: '#3457D5', fontWeight: 800 }}>•</span>
                                <span style={{ fontSize: '14px', color: '#334155', lineHeight: 1.6 }}>{feature}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div style={styles.sectionPanel}>
                    <h3 style={styles.sectionSubheading}>Payment method</h3>
                    <div style={styles.detailGrid}>
                        <div style={styles.detailCard}>
                            <div style={styles.detailLabel}>Default for renewal</div>
                            <div style={styles.detailValue}>{paymentMethodSummary}</div>
                            <div style={styles.statHelp}>{savedPaymentHint}</div>
                        </div>
                        <div style={styles.detailCard}>
                            <div style={styles.detailLabel}>Billing contact</div>
                            <div style={styles.detailValue}>{user.email}</div>
                            <div style={styles.statHelp}>{user.phone || 'Phone not configured yet'}</div>
                        </div>
                    </div>
                </div>
            </div>

            <div style={styles.sectionPanel}>
                <h3 style={styles.sectionSubheading}>Billing information</h3>
                <div style={styles.detailGrid}>
                    <div style={styles.detailCard}>
                        <div style={styles.detailLabel}>Company / account name</div>
                        <div style={styles.detailValue}>{user.organization_name || user.organizationname || user.name}</div>
                    </div>
                    <div style={styles.detailCard}>
                        <div style={styles.detailLabel}>Billing address</div>
                        <div style={styles.detailValue}>
                            {[user.address, cityValue, states.find((state) => state.isoCode === stateValue)?.name || stateValue, countries.find((country) => country.isoCode === countryValue)?.name || countryValue, zipValue]
                                .filter(Boolean)
                                .join(', ') || 'Not configured'}
                        </div>
                    </div>
                </div>
                <div style={styles.inlineActionRow}>
                    <button type="button" style={{ ...styles.actionButton, ...styles.secondaryAction }} onClick={handleEditProfile}>
                        Update billing information
                    </button>
                </div>
            </div>

            <div style={styles.sectionPanel}>
                <h3 style={styles.sectionSubheading}>Billing history</h3>
                <p style={styles.sectionDescription}>
                    Invoices and billing events for the workspace. Use this area to review successful payments, retry failed renewals, and stop future renewals if needed.
                </p>
                <div style={{ marginTop: '18px' }}>
                    {renderOrdersTable()}
                </div>
                {invoiceError && (
                    <div style={{ ...styles.alert, ...styles.alertError, marginTop: '18px' }}>
                        {invoiceError}
                    </div>
                )}
            </div>
        </div>
    );

    const renderTeamContent = () => (
        <div style={{ display: 'grid', gap: '18px' }}>
            <div style={styles.sectionPanel}>
                <h3 style={styles.sectionSubheading}>Workspace ownership and seats</h3>
                <p style={styles.sectionDescription}>
                    Corpusio is moving into a company-first SaaS model, so this section is where workspace owners, seats, and access controls should live.
                </p>
                <div style={{ ...styles.detailGrid, marginTop: '18px' }}>
                    <div style={styles.detailCard}>
                        <div style={styles.detailLabel}>Workspace owner</div>
                        <div style={styles.detailValue}>{user.name || user.email}</div>
                        <div style={styles.statHelp}>{user.email}</div>
                    </div>
                    <div style={styles.detailCard}>
                        <div style={styles.detailLabel}>Seat model</div>
                        <div style={styles.detailValue}>{teamSeatsSummary}</div>
                        <div style={styles.statHelp}>This workspace currently exposes a single authenticated owner in the dashboard experience.</div>
                    </div>
                    <div style={styles.detailCard}>
                        <div style={styles.detailLabel}>Organization</div>
                        <div style={styles.detailValue}>{user.organization_name || user.organizationname || 'Not configured'}</div>
                    </div>
                    <div style={styles.detailCard}>
                        <div style={styles.detailLabel}>Recommended roles</div>
                        <div style={styles.detailValue}>Owner, Admin, Billing Manager, Member, Viewer</div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderIntegrationsContent = () => (
        <div style={{ display: 'grid', gap: '18px' }}>
            <div style={styles.sectionPanel}>
                <h3 style={styles.sectionSubheading}>Integration runway</h3>
                <p style={styles.sectionDescription}>
                    External systems should be managed from one place so ingestion, billing, and operational trust stay aligned.
                </p>
                <div style={{ ...styles.detailGrid, marginTop: '18px' }}>
                    {[
                        ['Google Drive', 'Available for setup'],
                        ['SharePoint', 'Available for setup'],
                        ['Website crawl', 'Available for setup'],
                        ['Manual uploads', 'Ready'],
                    ].map(([label, status]) => (
                        <div key={label} style={styles.detailCard}>
                            <div style={styles.detailLabel}>{label}</div>
                            <div style={styles.detailValue}>{status}</div>
                            <div style={styles.statHelp}>Connector and sync controls should live here as this workspace rolls forward.</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderSecurityContent = () => (
        <div style={{ display: 'grid', gap: '18px' }}>
            <div style={styles.securityGrid}>
                <div style={styles.securityCard}>
                    <h3 style={styles.securityTitle}>Email verification</h3>
                    <p style={styles.securityText}>
                        {needsEmailVerification
                            ? 'This workspace still needs email verification before new billing actions can move forward.'
                            : isOauthAccount
                                ? 'This account is verified through Google sign-in.'
                                : 'Email verification is complete and ready for production workflows.'}
                    </p>
                    <div style={styles.inlineActionRow}>
                        {needsEmailVerification ? (
                            <button
                                type="button"
                                style={{ ...styles.actionButton, ...styles.primaryAction, opacity: emailSending ? 0.7 : 1 }}
                                onClick={handleSendEmailVerification}
                                disabled={emailSending}
                            >
                                {emailSending ? 'Sending…' : 'Send verification link'}
                            </button>
                        ) : (
                            <span style={styles.verifiedTag}>Verified</span>
                        )}
                    </div>
                    {(emailVerifyError || emailVerifyMessage) && (
                        <div
                            style={{
                                ...styles.inlineHelper,
                                marginTop: '12px',
                                ...(emailVerifyError ? styles.inlineHelperError : styles.inlineHelperSuccess),
                            }}
                        >
                            {emailVerifyError || emailVerifyMessage}
                        </div>
                    )}
                </div>

                <div style={styles.securityCard}>
                    <h3 style={styles.securityTitle}>Phone verification</h3>
                    <p style={styles.securityText}>
                        {isPhoneVerified
                            ? 'Phone verification is complete. Payment and invoice flows can use the verified number.'
                            : 'Verify your phone to unlock recurring billing safeguards and payment retries.'}
                    </p>
                    <div style={styles.inlineActionRow}>
                        {isPhoneVerified ? (
                            <span style={styles.verifiedTag}>Verified</span>
                        ) : (
                            <button type="button" style={{ ...styles.actionButton, ...styles.primaryAction }} onClick={handleRequirePhoneVerification}>
                                Verify phone
                            </button>
                        )}
                    </div>
                </div>

                <div style={styles.securityCard}>
                    <h3 style={styles.securityTitle}>Password and access</h3>
                    <p style={styles.securityText}>
                        Keep your workspace aligned with Corpusio’s password policy and rotate credentials without leaving the dashboard.
                    </p>
                    {isOauthAccount && (
                        <div style={{ ...styles.inlineHelper, ...styles.inlineHelperInfo, marginTop: '12px' }}>
                            This workspace was created with Google sign-in. You can set a local password here without entering a current one.
                        </div>
                    )}
                    <div style={styles.inlineActionRow}>
                        <button
                            type="button"
                            style={{ ...styles.actionButton, ...styles.primaryAction }}
                            onClick={() => setShowPasswordModal(true)}
                        >
                            Change password
                        </button>
                    </div>
                    {passwordMessage && (
                        <div style={{ ...styles.inlineHelper, ...styles.inlineHelperSuccess, marginTop: '12px' }}>{passwordMessage}</div>
                    )}
                </div>
            </div>

            <div style={styles.sectionPanel}>
                <h3 style={styles.sectionSubheading}>Compliance posture</h3>
                <p style={styles.sectionDescription}>
                    SSO, role-based access, audit logging, and document governance should sit alongside billing so administrators can trust the workspace in production.
                </p>
            </div>
        </div>
    );

    const renderSettingsContent = () => (
        <div style={{ display: 'grid', gap: '18px' }}>
            <div style={styles.sectionPanel}>
                <div style={{ ...styles.dashboardPageHeader, marginBottom: '16px' }}>
                    <div>
                        <h3 style={styles.sectionSubheading}>Workspace settings</h3>
                        <p style={styles.sectionDescription}>
                            Maintain the business identity, billing details, and operating profile that the workspace depends on.
                        </p>
                    </div>
                    <div style={styles.dashboardPageActions}>
                        <button style={{ ...styles.actionButton, ...styles.primaryAction }} onClick={handleEditProfile}>
                            Edit workspace profile
                        </button>
                    </div>
                </div>
                <div style={styles.detailGrid}>
                    <div style={styles.detailCard}>
                        <div style={styles.detailLabel}>Workspace name</div>
                        <div style={styles.detailValue}>{user.organization_name || user.organizationname || user.name || 'Not configured'}</div>
                    </div>
                    <div style={styles.detailCard}>
                        <div style={styles.detailLabel}>Account type</div>
                        <div style={styles.detailValue}>{userAccountType === 'company' ? 'Company' : 'Individual'}</div>
                    </div>
                    <div style={styles.detailCard}>
                        <div style={styles.detailLabel}>Email</div>
                        <div style={styles.detailValue}>{user.email}</div>
                    </div>
                    <div style={styles.detailCard}>
                        <div style={styles.detailLabel}>Phone</div>
                        <div style={styles.detailValue}>{user.phone || 'Not provided'}</div>
                    </div>
                    <div style={styles.detailCard}>
                        <div style={styles.detailLabel}>Industry</div>
                        <div style={styles.detailValue}>{user.industry || 'Not provided'}</div>
                    </div>
                    <div style={styles.detailCard}>
                        <div style={styles.detailLabel}>Team size</div>
                        <div style={styles.detailValue}>{user.team_size || user.teamsize || 'Not provided'}</div>
                    </div>
                    <div style={styles.detailCard}>
                        <div style={styles.detailLabel}>Billing address</div>
                        <div style={styles.detailValue}>
                            {[user.address, cityValue, states.find((state) => state.isoCode === stateValue)?.name || stateValue, countries.find((country) => country.isoCode === countryValue)?.name || countryValue, zipValue]
                                .filter(Boolean)
                                .join(', ') || 'Not provided'}
                        </div>
                    </div>
                    <div style={styles.detailCard}>
                        <div style={styles.detailLabel}>Workspace readiness</div>
                        <div style={styles.detailValue}>{profileCompletion}% complete</div>
                    </div>
                </div>
            </div>
        </div>
    );

    const PaymentStatusOverlay = ({ stage, message, error, onRetry, onClose }) => {
        if (stage === 'idle') return null;

        const isSuccess = stage === 'success';
        const isError = stage === 'error';
        const isProcessing = stage === 'processing' || stage === 'verifying';

        return (
            <div style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(255, 255, 255, 0.95)',
                zIndex: 2000,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '32px',
                textAlign: 'center',
                backdropFilter: 'blur(8px)',
            }}>
                {isProcessing && (
                    <>
                        <div className="animate-spin" style={{
                            width: '48px',
                            height: '48px',
                            border: '4px solid #f3f3f3',
                            borderTop: '4px solid #3457D5',
                            borderRadius: '50%',
                            marginBottom: '24px'
                        }}></div>
                        <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px', color: '#111827' }}>{message}</h3>
                        <p style={{ color: '#6B7280' }}>Please do not close this window or refresh the page.</p>
                    </>
                )}
                {isSuccess && (
                    <>
                        <div style={{
                            width: '64px',
                            height: '64px',
                            background: '#DCFCE7',
                            color: '#166534',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '32px',
                            marginBottom: '24px'
                        }}>✓</div>
                        <h3 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px', color: '#111827' }}>Payment Successful!</h3>
                        <p style={{ color: '#6B7280', marginBottom: '24px' }}>Your subscription has been activated successfully.</p>
                    </>
                )}
                {isError && (
                    <>
                        <div style={{
                            width: '64px',
                            height: '64px',
                            background: '#FEE2E2',
                            color: '#B91C1C',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '32px',
                            marginBottom: '24px'
                        }}>!</div>
                        <h3 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px', color: '#111827' }}>Payment Failed</h3>
                        <p style={{ color: '#6B7280', marginBottom: '24px' }}>{error || 'Something went wrong during the payment process.'}</p>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button type="button" onClick={onRetry} style={{ padding: '12px 24px', background: '#3457D5', color: '#fff', borderRadius: '12px', fontWeight: '600', border: 'none', cursor: 'pointer' }}>Try Again</button>
                            <button type="button" onClick={onClose} style={{ padding: '12px 24px', background: '#F3F4F6', color: '#374151', borderRadius: '12px', fontWeight: '600', border: 'none', cursor: 'pointer' }}>Close</button>
                        </div>
                    </>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div style={{ ...styles.container, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ fontSize: '18px', color: '#6E6E6E' }}>Loading dashboard...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ ...styles.container, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '16px' }}>
                <div style={{ fontSize: '18px', color: '#D81F26' }}>{error}</div>
                <button onClick={() => window.location.reload()} style={styles.logoutButton}>Retry</button>
                <a href="/" style={{ color: '#3457D5' }}>Go to Home</a>
            </div>
        )
    }

    if (!user) return null; // Should redirect

    return (
        <div className="dashboard-container" style={styles.container}>
            <PaymentStatusOverlay 
                stage={paymentStage} 
                message={statusMessage} 
                error={paymentError} 
                onRetry={() => setPaymentStage('idle')} 
                onClose={() => setPaymentStage('idle')} 
            />
            <div className="dashboard-header" style={styles.header}>
                <div className="dashboard-header-inner" style={styles.headerInner}>
                    <div>
                        <h1 className="welcome-text" style={styles.welcome}>Welcome back, {user.name?.split(' ')[0]}!</h1>
                        <p style={styles.subtitle}>{user.email}</p>
                    </div>
                </div>
            </div>

            {needsEmailVerification && (
                <div className="verify-banner" style={{ ...styles.verifyBanner, background: '#E0EAFF', borderColor: '#93C5FD', color: '#1E3A8A' }}>
                    <div>
                        <strong>Verify your workspace email address.</strong> Email verification is required before we can change subscriptions or accept new billing actions.
                        {(emailVerifyError || emailVerifyMessage) && (
                            <div
                                style={{
                                    ...styles.inlineHelper,
                                    ...(emailVerifyError ? styles.inlineHelperError : styles.inlineHelperSuccess)
                                }}
                            >
                                {emailVerifyError || emailVerifyMessage}
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        style={{ ...styles.verifyNowButton, opacity: emailSending ? 0.7 : 1 }}
                        onClick={handleSendEmailVerification}
                        disabled={emailSending}
                    >
                        {emailSending ? 'Sending…' : 'Send verification link'}
                    </button>
                </div>
            )}

            {!isPhoneVerified && (
                <div className="verify-banner" style={styles.verifyBanner}>
                    <div>
                        <strong>Verify your billing phone number.</strong> Phone verification is required before we can retry payments or protect recurring billing changes.
                    </div>
                    <button type="button" style={styles.verifyNowButton} onClick={handleRequirePhoneVerification}>
                        Verify now
                    </button>
                </div>
            )}

            <div className="dashboard-main" style={styles.main}>
                <div className="dashboard-shell" style={styles.dashboardShell}>
                    {!isTabletOrBelow && (
                    <aside className="dashboard-sidebar" style={styles.dashboardSidebar}>
                        <div style={styles.dashboardSidebarHeader}>
                            <h2 style={styles.dashboardSidebarTitle}>Corpusio workspace</h2>
                            <p style={styles.dashboardSidebarText}>
                                Run Corpusio like a product workspace: manage one subscription, one billing profile, your knowledge posture, and trust settings from a single operational view.
                            </p>
                        </div>
                        <div className="dashboard-sidebar-nav" style={styles.dashboardSidebarNav}>
                            {dashboardSections.map((section) => {
                                const active = section.id === activeView;
                                return (
                                    <button
                                        key={section.id}
                                        type="button"
                                        className="dashboard-sidebar-nav-button"
                                        style={{
                                            ...styles.dashboardNavButton,
                                            ...(active ? styles.dashboardNavButtonActive : {}),
                                        }}
                                        onClick={() => setActiveView(section.id)}
                                    >
                                        <div className="dashboard-sidebar-nav-label" style={styles.dashboardNavLabel}>
                                            <span className="dashboard-sidebar-nav-title">{section.label}</span>
                                            <span className="dashboard-sidebar-nav-badge" style={styles.dashboardNavBadge}>{section.badge}</span>
                                        </div>
                                        <div className="dashboard-sidebar-nav-meta" style={styles.dashboardNavMeta}>{section.description}</div>
                                    </button>
                                );
                            })}
                        </div>
                    </aside>
                    )}

                    <div className="dashboard-content-area" style={styles.dashboardContentArea}>
                        {isTabletOrBelow && (
                            <div style={styles.mobileSectionSwitcher}>
                                <div style={styles.mobileSectionHeader}>
                                    <div>
                                        <div style={styles.mobileSectionEyebrow}>Navigate workspace</div>
                                        <div style={styles.mobileSectionCaption}>
                                            Switch between billing, security, analytics, and workspace settings without scrolling back to the top.
                                        </div>
                                    </div>
                                    <span style={styles.mobileSectionBadge}>{currentSection.badge}</span>
                                </div>
                                <select
                                    value={activeView}
                                    onChange={(event) => setActiveView(event.target.value)}
                                    style={styles.mobileSectionSelect}
                                    aria-label="Switch dashboard section"
                                >
                                    {dashboardSections.map((section) => (
                                        <option key={section.id} value={section.id}>
                                            {section.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div className="dashboard-page-header" style={styles.dashboardPageHeader}>
                            <div>
                                <h2 className="dashboard-page-title" style={styles.dashboardPageTitle}>{currentSection.label}</h2>
                                <p className="dashboard-page-text" style={styles.dashboardPageText}>{currentSection.description}</p>
                            </div>
                            <div className="dashboard-page-actions" style={styles.dashboardPageActions}>{renderPageActions()}</div>
                        </div>

                        {orderAction && (
                            <div
                                style={{
                                    ...styles.alert,
                                    ...(orderAction.type === 'error' ? styles.alertError : styles.alertSuccess),
                                }}
                            >
                                {orderAction.text}
                            </div>
                        )}

                        {activeView === 'overview' && renderOverviewContent()}
                        {activeView === 'knowledge' && renderKnowledgeSourcesContent()}
                        {activeView === 'agents' && renderAgentsContent()}
                        {activeView === 'conversations' && renderConversationsContent()}
                        {activeView === 'analytics' && renderAnalyticsContent()}
                        {activeView === 'billing' && renderBillingContent()}
                        {activeView === 'team' && renderTeamContent()}
                        {activeView === 'integrations' && renderIntegrationsContent()}
                        {activeView === 'security' && renderSecurityContent()}
                        {activeView === 'settings' && renderSettingsContent()}
                    </div>
                </div>
            </div>



            {/* Profile Completion Modal */}
            {showPasswordModal && (
                <div style={styles.overlay}>
                    <div className="responsive-modal" style={{ ...styles.modal, position: 'relative' }}>
                        <button
                            type="button"
                            style={styles.closeButton}
                            onClick={() => setShowPasswordModal(false)}
                            aria-label="Close"
                        >
                            ×
                        </button>
                        <h2 style={styles.modalTitle}>Change Password</h2>
                        <p style={styles.modalSub}>Use the same security standard as account signup so your password stays consistent everywhere.</p>
                        {passwordError && (
                            <div style={{ marginBottom: '16px', color: '#D81F26', background: '#FFE0E0', padding: '12px', borderRadius: '8px' }}>
                                {passwordError}
                            </div>
                        )}
                        {passwordMessage && (
                            <div style={{ marginBottom: '16px', color: '#1E7A3E', background: '#E7F7EC', padding: '12px', borderRadius: '8px' }}>
                                {passwordMessage}
                            </div>
                        )}
                        <form onSubmit={handlePasswordSubmit}>
                            {!isOauthAccount && (
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Current Password</label>
                                    <input
                                        type="password"
                                        value={passwordForm.currentPassword}
                                        onChange={(e) => setPasswordForm((prev) => ({ ...prev, currentPassword: e.target.value }))}
                                        style={styles.input}
                                        required
                                    />
                                </div>
                            )}
                            <div style={styles.formGroup}>
                                <label style={styles.label}>New Password</label>
                                <input
                                    type="password"
                                    value={passwordForm.newPassword}
                                    onChange={(e) => setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))}
                                    style={styles.input}
                                    required
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Retype New Password</label>
                                <input
                                    type="password"
                                    value={passwordForm.confirmPassword}
                                    onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                                    style={styles.input}
                                    required
                                />
                            </div>
                            <div style={{ background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '14px 16px', marginBottom: '16px' }}>
                                <div style={{ fontSize: '12px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px' }}>
                                    Password requirements
                                </div>
                                <div style={{ display: 'grid', gap: '8px', fontSize: '13px' }}>
                                    <PasswordRequirement met={passwordForm.newPassword ? passwordChecks.hasMinLength : null} label="At least 12 characters" />
                                    <PasswordRequirement met={passwordForm.newPassword ? passwordChecks.hasLowercase : null} label="Include at least one lowercase letter (a-z)" />
                                    <PasswordRequirement met={passwordForm.newPassword ? passwordChecks.hasNumber : null} label="Include at least one number (1-9)" />
                                    <PasswordRequirement met={passwordForm.newPassword ? passwordChecks.hasSpecialChar : null} label="Include at least one special character" />
                                    <PasswordRequirement met={passwordForm.newPassword ? passwordChecks.isNotCommonPassword : null} label="Avoid common passwords" />
                                    <PasswordRequirement met={passwordForm.newPassword || passwordForm.confirmPassword ? passwordChecks.matchesConfirmation : null} label="Retyped password must match" />
                                    <PasswordRequirement met={passwordForm.newPassword ? passwordChecks.hasPrintableCharactersOnly : null} label="Use only ASCII characters with no spaces" />
                                </div>
                            </div>
                            <button
                                type="submit"
                                style={{ ...styles.submitButton, opacity: passwordSaving ? 0.7 : 1 }}
                                disabled={passwordSaving}
                            >
                                {passwordSaving ? 'Updating...' : 'Update Password'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
            {showProfileModal && (
                <div style={styles.overlay}>
                    <div className="responsive-modal" style={{ ...styles.modal, position: 'relative' }}>
                        <button
                            type="button"
                            style={styles.closeButton}
                            onClick={() => setShowProfileModal(false)}
                            aria-label="Close"
                        >
                            ×
                        </button>
                        <h2 style={styles.modalTitle}>Update Workspace Profile</h2>
                        <p style={styles.modalSub}>Keep the company identity, billing details, and operating profile current for this workspace.</p>

                        {modalError && (
                            <div style={{ marginBottom: '16px', color: '#D81F26', background: '#FFE0E0', padding: '12px', borderRadius: '8px' }}>
                                {modalError}
                            </div>
                        )}

                        <form onSubmit={handleModalSubmit}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Full Name *</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleModalChange}
                                    style={styles.input}
                                    placeholder="e.g. Priya Sharma"
                                    required
                                />
                                <div style={{ marginTop: '4px', fontSize: '12px', color: '#6B7280' }}>
                                    Used on payment invoices. Enter your full name (letters and spaces only).
                                </div>
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>Account Type</label>
                                <select
                                    name="accountType"
                                    value={formData.accountType}
                                    onChange={handleModalChange}
                                    style={styles.select}
                                >
                                    <option value="individual">Individual</option>
                                    <option value="company">Company</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>Phone Number *</label>
                                <div style={styles.phoneRow}>
                                    <select
                                        name="phoneCountry"
                                        value={formData.phoneCountry}
                                        onChange={handleModalChange}
                                        style={{ ...styles.select, flex: isMobile ? '1 1 auto' : '0 0 180px' }}
                                    >
                                        <option value="">Code</option>
                                        {countries.map(c => (
                                            <option key={`dial-${c.isoCode}`} value={c.isoCode}>
                                                +{c.phonecode || '??'} ({c.isoCode})
                                            </option>
                                        ))}
                                    </select>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleModalChange}
                                        style={{ ...styles.input, flex: 1 }}
                                        inputMode="numeric"
                                        placeholder="Phone number"
                                        required
                                    />
                                </div>
                                <div style={{ marginTop: '4px', fontSize: '12px', color: '#6B7280' }}>
                                    {getPhoneRequirementMessage(formData.phoneCountry || 'IN')}
                                </div>
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>Address *</label>
                                <input
                                    type="text"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleModalChange}
                                    style={styles.input}
                                    placeholder="123 Main St"
                                    required
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: isTabletOrBelow ? '1fr' : '1fr 1fr', gap: isMobile ? '14px' : '16px' }}>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Country *</label>
                                    <select
                                        name="country"
                                        value={formData.country}
                                        onChange={handleModalChange}
                                        style={styles.select}
                                        required
                                    >
                                        <option value="">Select Country...</option>
                                        {countries.map(c => (
                                            <option key={c.isoCode} value={c.isoCode}>{c.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>State *</label>
                                    <select
                                        name="state"
                                        value={formData.state}
                                        onChange={handleModalChange}
                                        style={styles.select}
                                        disabled={!formData.country}
                                        required
                                    >
                                        <option value="">Select State...</option>
                                        {states.map(s => (
                                            <option key={s.isoCode} value={s.isoCode}>{s.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: isTabletOrBelow ? '1fr' : '1fr 1fr', gap: isMobile ? '14px' : '16px' }}>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>City *</label>
                                    <select
                                        name="city"
                                        value={formData.city}
                                        onChange={handleModalChange}
                                        style={styles.select}
                                        disabled={!formData.state}
                                        required
                                    >
                                        <option value="">Select City...</option>
                                        {cities.map(city => (
                                            <option key={`${city.name}-${city.latitude}`} value={city.name}>
                                                {city.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Zip Code *</label>
                                    <input
                                        type="text"
                                        name="zipCode"
                                        value={formData.zipCode}
                                        onChange={handleModalChange}
                                        style={styles.input}
                                        required
                                    />
                                </div>
                            </div>

                            {formData.accountType === 'company' && (
                                <>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Organization Name</label>
                                        <input
                                            type="text"
                                            name="organizationName"
                                            value={formData.organizationName}
                                            onChange={handleModalChange}
                                            style={styles.input}
                                            required
                                        />
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Industry</label>
                                        <input
                                            type="text"
                                            name="industry"
                                            value={formData.industry}
                                            onChange={handleModalChange}
                                            style={styles.input}
                                        />
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Team Size</label>
                                        <select
                                            name="teamSize"
                                            value={formData.teamSize}
                                            onChange={handleModalChange}
                                            style={styles.select}
                                        >
                                            <option value="">Select size...</option>
                                            <option value="1-10">1-10</option>
                                            <option value="11-50">11-50</option>
                                            <option value="51-200">51-200</option>
                                            <option value="200+">200+</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            <button
                                type="submit"
                                style={{ ...styles.submitButton, opacity: saving ? 0.7 : 1 }}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Save & Continue'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
            {showOrderModal && (
                <OrderModal
                    onClose={handleOrderModalClose}
                    onSuccess={handleOrderSuccess}
                    initialValues={orderInitialValues}
                    user={normalizeWorkspaceUser(user || {})}
                    profileSeed={buildWorkspaceBillingPrefill(user || {})}
                    onRequirePhoneVerification={handleRequirePhoneVerification}
                    onRequireEmailVerification={handleRequireEmailVerification}
                    requiresEmailVerification={needsEmailVerification}
                />
            )}
            {retryMethodPending && (() => {
                const upiRetryAllowed = !retryMethodPending.amount || Number(retryMethodPending.amount) <= 10_000_000;
                const retryOptions = [
                    { value: 'all', label: 'All enabled', desc: 'Show every enabled Razorpay method' },
                    { value: 'card', label: '💳 Card', desc: 'Debit / Credit card mandate' },
                    ...(upiRetryAllowed ? [{ value: 'upi', label: '📱 UPI', desc: 'UPI Autopay mandate' }] : []),
                ];
                return (
                    <div style={styles.overlay}>
                        <div className="responsive-modal" style={{ ...styles.modal, maxWidth: '400px', position: 'relative' }}>
                            <button type="button" style={styles.closeButton} onClick={() => setRetryMethodPending(null)} aria-label="Close">×</button>
                            <h2 style={{ ...styles.modalTitle, fontSize: '20px' }}>Choose Payment Method</h2>
                            <p style={{ ...styles.modalSub, marginBottom: !upiRetryAllowed ? '8px' : '20px' }}>
                                Select how you'd like to retry payment for this order.
                            </p>
                            {!upiRetryAllowed && (
                                <p style={{ fontSize: '12px', color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '8px', padding: '8px 12px', marginBottom: '16px' }}>
                                    UPI Autopay is unavailable for orders above ₹1,00,000.
                                </p>
                            )}
                            <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
                                {retryOptions.map(({ value, label, desc }) => (
                                    <button
                                        key={value}
                                        type="button"
                                        onClick={() => executeRetryPayment(retryMethodPending, value)}
                                        style={{
                                            flex: 1,
                                            padding: '16px 12px',
                                            borderRadius: '12px',
                                            border: '2px solid #E5E7EB',
                                            background: '#FAFAFA',
                                            cursor: 'pointer',
                                            textAlign: 'center',
                                            transition: 'all 0.15s',
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#3457D5'; e.currentTarget.style.background = '#EFF6FF'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.background = '#FAFAFA'; }}
                                    >
                                        <div style={{ fontSize: '20px', marginBottom: '4px' }}>{label}</div>
                                        <div style={{ fontSize: '12px', color: '#6B7280' }}>{desc}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            })()}
            {showPhoneVerifyModal && (
                <div style={styles.overlay}>
                    <div className="responsive-modal" style={{ ...styles.modal, position: 'relative' }}>
                        <button
                            type="button"
                            style={styles.closeButton}
                            onClick={() => setShowPhoneVerifyModal(false)}
                            aria-label="Close"
                        >
                            ×
                        </button>
                        <h2 style={styles.modalTitle}>Verify your phone</h2>
                        <p style={styles.modalSub}>
                            Request a verification code and enter it below. We’ll use it to secure future payments and invoices.
                        </p>
                        {phoneVerifyError && (
                            <div style={{ ...styles.alert, ...styles.alertError }}>{phoneVerifyError}</div>
                        )}
                        {phoneVerifyMessage && (
                            <div style={{ ...styles.alert, ...styles.alertSuccess }}>{phoneVerifyMessage}</div>
                        )}
                        <div style={styles.phoneVerifyActions}>
                            <button
                                type="button"
                                style={{ ...styles.phoneVerifyButton, ...styles.primaryButton, opacity: phoneSending ? 0.7 : 1 }}
                                onClick={sendPhoneVerificationCode}
                                disabled={phoneSending}
                            >
                                {phoneSending ? 'Sending code…' : 'Send verification code'}
                            </button>
                            <button
                                type="button"
                                style={{ ...styles.phoneVerifyButton, ...styles.secondaryButton }}
                                onClick={() => {
                                    setShowPhoneVerifyModal(false);
                                    setPhoneVerifyCode('');
                                    setPhoneVerifyError('');
                                    setPhoneVerifyMessage('');
                                }}
                            >
                                Close
                            </button>
                        </div>
                        <form onSubmit={handleVerifyPhoneCodeSubmit} style={{ marginTop: '24px' }}>
                            <label style={styles.label}>Enter 6-digit code</label>
                            <input
                                style={styles.codeInput}
                                value={phoneVerifyCode}
                                onChange={(e) => setPhoneVerifyCode(e.target.value.replace(/\D+/g, '').slice(0, 6))}
                                placeholder="123456"
                                inputMode="numeric"
                            />
                            <button
                                type="submit"
                                style={{
                                    ...styles.submitButton,
                                    marginTop: '16px',
                                    opacity: phoneVerifying || phoneVerifyCode.length !== 6 ? 0.7 : 1
                                }}
                                disabled={phoneVerifying || phoneVerifyCode.length !== 6}
                            >
                                {phoneVerifying ? 'Verifying…' : 'Verify phone'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;
